import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight, ArrowUpRight, Star, Trophy, ArrowLeftRight, History, ChevronLeft, ChevronRight, Cake, PartyPopper, Link2, Crown, Award, Goal, Sparkles, Medal, Flame } from "lucide-react";
import { TeamBadge } from "../TeamBadge";
import { PlayerAvatar } from "../PlayerAvatar";
import { TEAM_INFO, teamColor as liveTeamColor } from "../../lib/teamMap";
import { useTeamLookup } from "../../lib/teamLookup";
import { assetUrl } from "../../lib/api";
import { useCreatorMode } from "../../lib/CreatorModeContext";

const tname = (id) => TEAM_INFO[id]?.name || (id || "").toUpperCase();
const tabbr = (id) => TEAM_INFO[id]?.abbr || (id || "").toUpperCase();
const tnick = (id) => (tname(id).split(/\s+/).slice(-1)[0] || "").toUpperCase();
const tcol = (id) => liveTeamColor(id);
// Transliterate accented characters to their closest ASCII letter so display
// fonts that lack accented glyphs (e.g. Anton) never render a name as blank —
// "Söderberg" → "Soderberg". Applied to every rendered player name.
const _DEACC = { "ø": "o", "Ø": "O", "ł": "l", "Ł": "L", "đ": "d", "Đ": "D", "æ": "ae", "Æ": "AE", "œ": "oe", "Œ": "OE", "ß": "ss", "þ": "th", "ð": "d" };
const deaccent = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[øØłŁđĐæÆœŒßþð]/g, (c) => _DEACC[c] || c);
// Reused verbatim from the Schedule page (PWHLSchedule STAGE_BADGE): grey / purple / gold.
const STAGE_COL = { preseason: "#94A3B8", regular: "#9D4CDD", playoffs: "#FBBF24" };
const dOnly = (v) => String(v || "").slice(0, 10);

// A shared shell that mirrors StoryFeedCard's gradient + aspect handling so the
// new Game templates sit visually alongside the newspaper cards.
function Shell({ color, orientation = "v", square = false, children, testId, bg: bgOverride = null }) {
    const vertical = orientation === "v" || orientation === "slimv";
    const bg = { background: bgOverride
        ? `radial-gradient(130% 150% at 82% 8%, ${color}2e 0%, transparent 54%), ${bgOverride}`
        : `radial-gradient(130% 150% at 82% 8%, ${color}2e 0%, transparent 54%), linear-gradient(160deg, #101114 0%, #08090B 100%)` };
    return (
        <article data-testid={testId} data-orientation={orientation}
            className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 hover:border-white/20 transition-colors p-5 sm:p-6 shadow-xl shadow-black/40 flex flex-col ${vertical ? "aspect-[4/5]" : "h-full min-h-[236px]"}`}
            style={bg}>
            {children}
        </article>
    );
}

const Pill = ({ label, color, live }) => (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] sm:text-[10px] font-black uppercase tracking-wider"
        style={{ color, background: `${color}1a`, border: `1px solid ${color}59` }}>
        {live && <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: color }} /><span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: color }} /></span>}
        {label}
    </span>
);
const Pills = ({ pills, color, show }) => show("pills") && !!(pills || []).length && (
    <div className="flex flex-wrap items-center gap-1.5">{pills.map((p, i) => <Pill key={i} label={p} color={color} live={p.toLowerCase() === "live"} />)}</div>
);

// Crest inside a team-colour ring / bare logo / black circle — style-driven.
function Crest({ id, px = 84, style = "ring" }) {
    if (!id) return null;
    if (style === "logo") return <StoryLogo id={id} px={px} />;
    const ring = style === "ring";
    return (
        <div className="rounded-full" style={ring ? { padding: 3, background: `linear-gradient(160deg, ${tcol(id)}, ${tcol(id)}44)`, width: px + 6, height: px + 6 } : { width: px, height: px }}>
            <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center" style={{ width: px, height: px }}>
                <TeamBadge teamId={id} size="xl" asLink={false} />
            </div>
        </div>
    );
}
// Bare crest for the "logo" style. SAME square box + size as before (kept
// centralised, unchanged size) — the ONLY difference is the logo is anchored to
// the BOTTOM of the box (object-position:bottom). This makes a WIDE logo (e.g.
// Boston's B) rest on the exact same baseline as a TALL crest (e.g. Victoire's
// shield) instead of floating in the vertical middle and reading as "lower".
function StoryLogo({ id, px = 84 }) {
    const { any } = useTeamLookup();
    const t = any(id) || {};
    const url = t.logo_url ? assetUrl(t.logo_url) : null;
    const scale = (t.logo_scale ?? 100) / 100;
    return (
        <div className="flex items-center justify-center" style={{ width: px, height: px }} data-testid={`story-logo-${id}`}>
            {url
                ? <img src={url} alt={t.name || id} className="w-full h-full"
                    style={{ objectFit: "contain", objectPosition: "center bottom", transform: `scale(calc(var(--logo-scale, 1) * ${scale}))`, opacity: t.logo_opacity ?? 1 }} />
                : <TeamBadge teamId={id} size="xl" asLink={false} />}
        </div>
    );
}
function Face({ subject, color, px = 96 }) {
    return (
        <div className="rounded-full p-[3px]" style={{ background: `linear-gradient(160deg, ${color}, ${color}44)`, width: px + 6, height: px + 6 }}>
            <div className="rounded-full overflow-hidden bg-[#0E1110] flex items-center justify-center" style={{ width: px, height: px }}>
                <PlayerAvatar photo={subject?.photo_url} name={subject?.name} playerId={subject?.id || ""} sizePx={px} showTierRing={false} />
            </div>
        </div>
    );
}

// Tiny SOURCE symbol that lives INSIDE each template footer (not an overlay).
// Public: a small link to the source. Creator Mode: opens a compact editor to
// correct the label/URL (session-local — the user says it's rarely needed).
function SourceSymbol({ source }) {
    const { creatorMode } = useCreatorMode();
    const [editing, setEditing] = React.useState(false);
    const [label, setLabel] = React.useState(source?.label || "");
    const [href, setHref] = React.useState(source?.href || "");
    const has = !!(source && source.href && source.href !== "#");
    if (!has && !creatorMode) return null;
    const dot = "inline-flex items-center justify-center h-5 w-5 rounded-full bg-white/[0.06] border border-white/15 text-white/45 hover:text-white hover:border-white/30 transition-colors shrink-0";
    if (creatorMode && editing) {
        return (
            <span className="relative inline-block" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
                <span className="absolute bottom-7 right-0 z-30 w-52 rounded-lg bg-[#0E0F16] border border-white/15 shadow-xl shadow-black/60 p-2 space-y-1.5 text-left normal-case">
                    <span className="block text-[9px] font-black uppercase tracking-wider text-white/50">Edit source</span>
                    <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" data-testid="tpl-source-label" className="w-full rounded bg-black/40 border border-white/15 px-2 py-1 text-[11px] text-white outline-none focus:border-[#EC4899]" />
                    <input value={href} onChange={(e) => setHref(e.target.value)} placeholder="URL / path" data-testid="tpl-source-href" className="w-full rounded bg-black/40 border border-white/15 px-2 py-1 text-[11px] text-white outline-none focus:border-[#EC4899]" />
                    <span className="flex items-center gap-1.5">
                        <button type="button" data-testid="tpl-source-save" onClick={() => { if (source) { source.label = label; source.href = href; } setEditing(false); }} className="rounded bg-white/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white hover:bg-white/20">Save</button>
                        <button type="button" data-testid="tpl-source-cancel" onClick={() => { setEditing(false); setLabel(source?.label || ""); setHref(source?.href || ""); }} className="text-[10px] font-bold uppercase tracking-wider text-white/45 px-1 hover:text-white">Cancel</button>
                    </span>
                </span>
                <button type="button" className={dot} title="Edit source"><Link2 className="h-3 w-3" /></button>
            </span>
        );
    }
    if (creatorMode) {
        return <button type="button" data-testid="tpl-source-edit" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }} className={dot} title={has ? `Source: ${source.label} (tap to edit)` : "Add source"}><Link2 className="h-3 w-3" /></button>;
    }
    if (/^https?:\/\//i.test(source.href || "")) {
        return <a href={source.href} target="_blank" rel="noopener noreferrer" data-testid="tpl-source-link" title={`Source: ${source.label}`} className={dot} onClick={(e) => e.stopPropagation()}><Link2 className="h-3 w-3" /></a>;
    }
    return <Link to={source.href} data-testid="tpl-source-link" title={`Source: ${source.label}`} className={dot} onClick={(e) => e.stopPropagation()}><Link2 className="h-3 w-3" /></Link>;
}

const MoreLink = ({ color, label = "More", href = null, source = null }) => {
    const cls = "inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider group-hover:gap-1.5 transition-all";
    const inner = <>{label} <ArrowRight className="h-3.5 w-3.5" /></>;
    const link = href
        ? <Link to={href} className={cls} style={{ color }} data-testid="card-more-link">{inner}</Link>
        : <span className={cls} style={{ color }}>{inner}</span>;
    return <span className="inline-flex items-center gap-2">{source && <SourceSymbol source={source} />}{link}</span>;
};

// ---- Team vs Team (preview OR result). Upcoming → home colour; postgame →
// winning-team colour (set upstream via s.teamColor). Shows BOTH crests. ----
export function GameVsCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const home = s.teamId, away = s.oppId;
    const post = !!s.score;
    // Result background toggle: "winner" (default) tints the card the winning
    // team's colour; "home" always uses the home team's colour.
    const bgMode = s._resultBg || "winner";
    const color = (post && bgMode === "home") ? tcol(home) : (s.teamColor || tcol(home));
    const hs = s.score?.home, as = s.score?.away;
    const homeWon = post && hs >= as;
    const px = orientation === "v" ? 92 : 78;
    // V2 skin = "Jumbotron" (LED scoreboard look) — same live data, new design.
    if ((s._vsSkin || "v1") === "v2") {
        const cpx = orientation === "v" ? 74 : 60;
        return (
            <Shell color={color} orientation={orientation} square={square} testId={testId}>
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.18) 0.5px, transparent 1px)", backgroundSize: "4px 4px", opacity: 0.4 }} />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(transparent 0 2px, rgba(0,0,0,0.28) 2px 4px)" }} />
                <div className="absolute inset-1.5 rounded-xl pointer-events-none" style={{ boxShadow: `inset 0 0 0 1px ${color}33, inset 0 0 34px ${color}18` }} />
                <div className="relative z-10 flex flex-col h-full items-center justify-center gap-3 py-2 px-2">
                    {show("status") && <span className="font-story-punch uppercase text-xs sm:text-sm tracking-[0.3em] text-center" style={{ color, textShadow: `0 0 10px ${color}88` }}>{post ? (s.status || "FINAL") : (s.when || "PREVIEW")}</span>}
                    <div className="flex items-center justify-center gap-4 sm:gap-6">
                        <div className="flex flex-col items-center gap-1.5 min-w-0">
                            {show("face") && <Crest id={away} px={cpx} style={s._crestAway || "logo"} />}
                            {show("names") && <span className="font-story-punch text-white text-[11px] sm:text-xs tracking-widest whitespace-nowrap">{tabbr(away)}</span>}
                        </div>
                        {post
                            ? (show("score") && <span className="font-story-punch text-5xl sm:text-6xl tabular-nums leading-none shrink-0" style={{ color, textShadow: `0 0 18px ${color}99` }}>{as}<span className="text-white/25 mx-1.5">–</span>{hs}</span>)
                            : (show("vs") && <span className="font-story-punch text-3xl sm:text-4xl italic text-white/70 shrink-0">@</span>)}
                        <div className="flex flex-col items-center gap-1.5 min-w-0">
                            {show("face") && <Crest id={home} px={cpx} style={s._crestHome || "logo"} />}
                            {show("names") && <span className="font-story-punch text-white text-[11px] sm:text-xs tracking-widest whitespace-nowrap">{tabbr(home)}</span>}
                        </div>
                    </div>
                    {!post && show("records") && (s.records?.[1] || s.records?.[0]) && (
                        <div className="flex items-center gap-6 text-[10px] uppercase tracking-wide text-white/45">
                            <span>{s.records?.[1] || ""}</span><span>{s.records?.[0] || ""}</span>
                        </div>
                    )}
                    {show("footer") && <span className="font-story-punch uppercase text-[10px] tracking-[0.28em] text-white/40 text-center truncate max-w-full">{post ? `${tabbr(away)} @ ${tabbr(home)}` : (s.venue || "")}</span>}
                </div>
            </Shell>
        );
    }
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={color} show={show} />
                {show("status") && <span className="text-[10px] font-black uppercase tracking-widest text-white/45">{post ? (s.status || "Final") : (s.when || "")}</span>}
            </div>
            {show("round") && s.roundLabel && (
                <div className="flex justify-center mt-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] shadow-lg"
                        style={{ background: color, color: "#FFFFFF" }}>
                        {s.roundLabel}
                    </span>
                </div>
            )}
            <div className="flex-1 flex items-center justify-center gap-3 sm:gap-5 py-4">
                {/* AWAY team first — hockey convention is AWAY @ HOME. */}
                <div className="flex flex-col items-center gap-2 min-w-0">
                    {show("face") && <Crest id={away} px={px} style={s._crestAway || "logo"} />}
                    <div className="text-center">
                        {show("names") && <div className="font-story-punch uppercase leading-none text-lg sm:text-xl text-white whitespace-nowrap h-6 sm:h-7 flex items-center justify-center">{tnick(away)}</div>}
                        {post ? (<>
                            {show("score") && <div className="font-story-punch text-4xl sm:text-5xl tabular-nums mt-1 leading-none" style={{ color: !homeWon ? "#ffffff" : "#ffffff99" }}>{as}</div>}
                            {show("records") && s.records?.[1] && <div className="text-[10px] uppercase tracking-wide text-white/45 mt-1 tabular-nums">{s.records[1]}</div>}
                        </>)
                            : (show("records") && s.records?.[1] && <div className="text-[10px] uppercase tracking-wide text-white/45 mt-0.5">{s.records[1]}</div>)}
                    </div>
                </div>
                <div className="flex flex-col items-center">
                    {show("vs") && (<>
                        <span className="font-story-punch text-2xl sm:text-3xl italic text-white">{post ? "VS" : "@"}</span>
                    </>)}
                </div>
                <div className="flex flex-col items-center gap-2 min-w-0">
                    {show("face") && <Crest id={home} px={px} style={s._crestHome || "logo"} />}
                    <div className="text-center">
                        {show("names") && <div className="font-story-punch uppercase leading-none text-lg sm:text-xl text-white whitespace-nowrap h-6 sm:h-7 flex items-center justify-center">{tnick(home)}</div>}
                        {post ? (<>
                            {show("score") && <div className="font-story-punch text-4xl sm:text-5xl tabular-nums mt-1 leading-none" style={{ color: homeWon ? "#ffffff" : "#ffffff99" }}>{hs}</div>}
                            {show("records") && s.records?.[0] && <div className="text-[10px] uppercase tracking-wide text-white/45 mt-1 tabular-nums">{s.records[0]}</div>}
                        </>)
                            : (show("records") && s.records?.[0] && <div className="text-[10px] uppercase tracking-wide text-white/45 mt-0.5">{s.records[0]}</div>)}
                    </div>
                </div>
            </div>
            {show("footer") && (
                <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 truncate">{post ? `${tabbr(away)} @ ${tabbr(home)}` : (s.venue || "")}</span>
                    {footerExtra || <MoreLink source={s.sourceRef} color={tcol(home) || color} />}
                </div>
            )}
        </Shell>
    );
}

// ---- Playoff / Preseason SERIES card. Same live matchup data as game_vs, plus
// a fixed banner stating the bracket round (GOLD for playoffs, GREY for
// preseason — never team-coloured, so seasons read at a glance) and a row of
// win "pips" under each team (filled white per series win, best-of-5 → 3 to win).
export function GameSeriesCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const home = s.teamId, away = s.oppId;
    const post = !!s.score;
    const stage = s.stage || "playoffs";
    const isPre = stage === "preseason";
    const color = s.teamColor || tcol(home);
    const hs = s.score?.home, as = s.score?.away;
    const homeWon = post && hs >= as;
    const px = orientation === "v" ? 84 : 70;
    const sr = s.series || {};
    // Pips = WINS NEEDED to clinch (best-of-5 → first to 3), never the series length.
    const total = sr.winsNeeded || Math.ceil((sr.bestOf || 5) / 2);
    const roundName = sr.round || (isPre ? "Preseason" : "Playoffs");
    const bannerBg = isPre ? "linear-gradient(90deg,#C3CDD9,#94A3B8)" : "linear-gradient(90deg,#F6C453,#E4A93C)";
    const WinPips = ({ wins }) => (
        <div className="flex items-center justify-center gap-1.5 mt-2" data-testid={testId ? `${testId}-pips` : undefined}>
            {Array.from({ length: total }).map((_, i) => (
                <span key={i} className="rounded-full border-2 transition-colors" style={{ width: 13, height: 13, borderColor: "rgba(255,255,255,0.55)", background: i < (wins || 0) ? "#ffffff" : "transparent" }} />
            ))}
        </div>
    );
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={color} show={show} />
                {show("status") && <span className="text-[10px] font-black uppercase tracking-widest text-white/45">{post ? (s.status || "Final") : (s.when || "")}</span>}
            </div>
            {show("round") && (
                <div className="flex justify-center mt-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] shadow-lg"
                        style={{ background: bannerBg, color: "#12100A" }} data-testid={testId ? `${testId}-round` : undefined}>
                        {roundName}
                    </span>
                </div>
            )}
            <div className="flex-1 flex items-center justify-center gap-3 sm:gap-5 py-3">
                <div className="flex flex-col items-center gap-2 min-w-0">
                    {show("face") && <Crest id={away} px={px} style={s._crestAway || "logo"} />}
                    <div className="text-center">
                        {show("names") && <div className="font-story-punch uppercase leading-none text-lg sm:text-xl text-white whitespace-nowrap h-6 sm:h-7 flex items-center justify-center">{tnick(away)}</div>}
                        {post && show("score") && <div className="font-story-punch text-3xl sm:text-4xl tabular-nums mt-1 leading-none" style={{ color: !homeWon ? "#ffffff" : "#ffffff99" }}>{as}</div>}
                        {show("pips") && !isPre && <WinPips wins={sr.awayWins} />}
                    </div>
                </div>
                <div className="flex flex-col items-center">
                    {show("vs") && <span className="font-story-punch text-2xl sm:text-3xl italic text-white">{post ? "VS" : "@"}</span>}
                </div>
                <div className="flex flex-col items-center gap-2 min-w-0">
                    {show("face") && <Crest id={home} px={px} style={s._crestHome || "logo"} />}
                    <div className="text-center">
                        {show("names") && <div className="font-story-punch uppercase leading-none text-lg sm:text-xl text-white whitespace-nowrap h-6 sm:h-7 flex items-center justify-center">{tnick(home)}</div>}
                        {post && show("score") && <div className="font-story-punch text-3xl sm:text-4xl tabular-nums mt-1 leading-none" style={{ color: homeWon ? "#ffffff" : "#ffffff99" }}>{hs}</div>}
                        {show("pips") && !isPre && <WinPips wins={sr.homeWins} />}
                    </div>
                </div>
            </div>
            {show("footer") && (
                <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 truncate">{post ? `${tabbr(away)} @ ${tabbr(home)}` : (s.venue || "")}</span>
                    {footerExtra || <MoreLink source={s.sourceRef} color={tcol(home) || color} />}
                </div>
            )}
        </Shell>
    );
}

// ---- Creator-only calendar date picker for the Today's Games template. ----
// This lives ONLY in the Creator Tools panel — never in the public graphic. The
// game-count circles reuse the Schedule page's exact stage colours (grey PRE /
// purple RS / gold PO) or the home-team colour, driven by the Stage/Team toggle.
export function TodayGamesCalendar({ schedule = [], value, mode = "stage", onPick, onModeChange }) {
    const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const sel = value || todayStr;
    const byDate = React.useMemo(() => {
        const m = {};
        (schedule || []).forEach((g) => {
            const d = dOnly(g.date || g.game_date);
            if (!d || !g.home_team_id) return;
            (m[d] = m[d] || []).push(g);
        });
        return m;
    }, [schedule]);
    const [vm, setVm] = React.useState(sel.slice(0, 7));
    const [Y, M] = vm.split("-").map(Number);
    const startDow = new Date(Y, M - 1, 1).getDay();
    const dim = new Date(Y, M, 0).getDate();
    const cells = []; for (let i = 0; i < startDow; i++) cells.push(null); for (let d = 1; d <= dim; d++) cells.push(d);
    const dstr = (d) => `${vm}-${String(d).padStart(2, "0")}`;
    const monthLabel = new Date(Y, M - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
    const shiftMonth = (delta) => { const nd = new Date(Y, M - 1 + delta, 1); setVm(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`); };
    const circleColor = (gs) => mode === "team" ? tcol(gs[0].home_team_id) : (STAGE_COL[gs[0].stage] || STAGE_COL.regular);
    return (
        <div className="rounded-lg border border-white/10 bg-black/30 p-2" data-testid="today-calendar">
            <div className="flex items-center justify-between gap-2 mb-1">
                <button type="button" onClick={() => shiftMonth(-1)} data-testid="today-cal-prev" className="h-5 w-5 inline-flex items-center justify-center rounded text-white/50 hover:text-white hover:bg-white/10"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <span className="text-[10px] font-black uppercase tracking-widest text-white/80" data-testid="today-cal-month">{monthLabel}</span>
                <button type="button" onClick={() => shiftMonth(1)} data-testid="today-cal-next" className="h-5 w-5 inline-flex items-center justify-center rounded text-white/50 hover:text-white hover:bg-white/10"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex items-center justify-center gap-1 mb-1.5">
                <button type="button" onClick={() => onModeChange && onModeChange("stage")} data-testid="today-mode-stage" className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${mode !== "team" ? "bg-white/15 text-white" : "text-white/40"}`}>Stage</button>
                <button type="button" onClick={() => onModeChange && onModeChange("team")} data-testid="today-mode-team" className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${mode === "team" ? "bg-white/15 text-white" : "text-white/40"}`}>Team</button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[7px] font-black uppercase text-white/25">{d}</div>)}
                {cells.map((d, i) => {
                    if (d === null) return <div key={i} />;
                    const ds = dstr(d); const gs = byDate[ds] || []; const has = gs.length > 0;
                    const isSel = ds === sel; const isToday = ds === todayStr;
                    return (
                        <button key={i} type="button" onClick={() => onPick && onPick(ds)} data-testid={has ? `today-cal-day-${ds}` : undefined}
                            className={`relative aspect-square flex items-center justify-center rounded ${isSel ? "ring-1 ring-white" : ""}`}>
                            {has
                                ? <span className="inline-flex h-[15px] min-w-[15px] px-0.5 items-center justify-center rounded-full text-[8px] font-black text-white leading-none" style={{ background: circleColor(gs) }} title={`${gs.length} game${gs.length === 1 ? "" : "s"}`}>{gs.length}</span>
                                : <span className={`text-[8px] ${isToday ? "text-white font-bold" : "text-white/30"}`}>{d}</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---- Today's Games slate — a stacked list of matchups with crests. ----
// The date + colour mode are read from the Creator override (saved per
// template|format) so the PUBLIC graphic matches the Creator Tools selection 1:1.
// Shared matchup row — AWAY | center | HOME, always away-then-home, vertically
// centred (fixes the lop-sided team name). The logo|name + centre columns size to
// their content (auto), so each team's crest+abbr reacts to the text length.
export function MatchupRow({ a, h, center, color = "#38BDF8", lg = 28, padY = 8, testId }) {
    return (
        <div className="grid items-center gap-2 w-full" style={{ gridTemplateColumns: `1fr ${lg}px auto ${lg}px 1fr`, paddingTop: padY, paddingBottom: padY }} data-testid={testId}>
            <span className="font-story-punch uppercase text-sm text-white/85 text-right leading-none self-center whitespace-nowrap">{tabbr(a)}</span>
            <div className="justify-self-center flex items-center justify-center" style={{ width: lg, height: lg }}><TeamBadge teamId={a} size="sm" asLink={false} /></div>
            <span className="justify-self-center self-center text-[10px] font-black uppercase tracking-wide tabular-nums px-2 py-1 rounded text-white text-center whitespace-nowrap leading-none" style={{ background: `${color}14` }}>{center}</span>
            <div className="justify-self-center flex items-center justify-center" style={{ width: lg, height: lg }}><TeamBadge teamId={h} size="sm" asLink={false} /></div>
            <span className="font-story-punch uppercase text-sm text-white/85 text-left leading-none self-center whitespace-nowrap">{tabbr(h)}</span>
        </div>
    );
}

export function GameTodayCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra, schedule = null }) {
    const show = (k) => !hideEls.includes(k);
    const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const colorMode = s._todayMode || "stage";
    // Live schedule (all seasons) → date→games map; auto-updates when schedule changes.
    const byDate = React.useMemo(() => {
        const m = {};
        (schedule || []).forEach((g) => {
            const d = dOnly(g.date || g.game_date);
            if (!d || !g.home_team_id) return;
            (m[d] = m[d] || []).push(g);
        });
        return m;
    }, [schedule]);
    const hasReal = Object.keys(byDate).length > 0;
    // Anchor date: an explicit Creator date wins. Otherwise use today — but if
    // today has no slate (e.g. offseason "Jul 21 · 0 games"), snap to the nearest
    // real game day (next upcoming, else most recent) so we never show an empty card.
    let sel = s._todayDate || todayStr;
    if (hasReal && !s._todayDate && !byDate[sel]) {
        const days = Object.keys(byDate).sort();
        sel = days.find((d) => d >= sel) || days[days.length - 1] || sel;
    }
    let dayGames = byDate[sel] || [];
    // Per-game preview (grouped with its Starting-In-Net duo): show only that game.
    if (s._onlyGameId) dayGames = dayGames.filter((g) => (g.id || g.game_id) === s._onlyGameId);
    const state = s._todayState || "preview";   // "preview" = scheduled slate · "recap" = final results

    const rows = hasReal
        ? dayGames.map((g) => {
            const finalScore = (g.status === "Final" && g.home_score != null) ? `${g.away_score}-${g.home_score}` : null;
            return { a: g.away_team_id, h: g.home_team_id, stage: g.stage,
                // Recap shows the final score; preview shows the scheduled tip-off time.
                score: state === "recap" ? finalScore : null,
                time: g.time || g.start_time || (g.status && g.status !== "Final" ? g.status : "@") };
        })
        : (s.games || []).map((g) => ({ ...g, score: state === "recap" ? (g.score || null) : null }));

    // Background reuses the Schedule-page stage colours (grey/purple/gold) OR the
    // first game's home-team colour, driven by the Creator Stage/Team toggle.
    const firstG = dayGames[0];
    const stageKey = firstG?.stage || "regular";
    const color = hasReal
        ? (colorMode === "team" && firstG ? tcol(firstG.home_team_id) : (STAGE_COL[stageKey] || STAGE_COL.regular))
        : (s.teamColor || "#38BDF8");
    const selLabel = new Date(sel + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const stateLabel = state === "recap" ? "Final" : "Upcoming";
    const stageLabel = { preseason: "Preseason", pre: "Preseason", regular: "Regular Season", playoffs: "Playoffs", playoff: "Playoffs", final: "Playoffs" }[String(stageKey).toLowerCase()] || "Regular Season";
    const posJustify = s._todayPos === "center" ? "justify-center" : s._todayPos === "bottom" ? "justify-end" : "justify-start";

    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            {show("header") && <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0" style={{ background: `${color}1f`, color }}><CalendarDays className="h-3.5 w-3.5" /></span>
                <span className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">{hasReal ? selLabel : (s.kicker || (state === "recap" ? "Today's Results" : "Today's Games"))}</span>
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded whitespace-nowrap" style={{ background: color, color: "#0A0B0D" }}>{stateLabel}</span>
                {hasReal && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded whitespace-nowrap" style={{ background: `${color}22`, color }} data-testid="today-stage-label">{stageLabel}</span>}
                {show("count") && <span className="text-[9px] font-black uppercase tracking-widest text-white/45 tabular-nums whitespace-nowrap" data-testid="today-count">{rows.length} {rows.length === 1 ? "game" : "games"}</span>}
            </div>}

            <div className={`mt-2 flex-1 min-h-0 overflow-y-auto flex flex-col ${posJustify} divide-y divide-white/[0.06]`}>
                {rows.length === 0 && <div className="py-4 text-center text-[10px] uppercase tracking-widest text-white/30">No games this day</div>}
                {rows.map((g, i) => {
                    const lg = s._todayLogo ?? 28;
                    const padY = s._todayPadY ?? 8;
                    // Center = "@" for the scheduled slate, the "#-#" score for
                    // the results slate (matches the per-template convention).
                    return <MatchupRow key={i} a={g.a} h={g.h} center={state === "recap" ? (g.score || "@") : "@"} color={color} lg={lg} padY={padY} testId={`game-today-row-${i}`} />;
                })}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">{s.source || "PWHL"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} label="Full slate" />}
            </div>}
        </Shell>
    );
}

// ---- Three Stars — 3 player faces centred, with 1/2/3 badges. ----
export function GameStars3Card({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || "#F5C518";
    const stars = (s.stars || []).slice(0, 3);
    const px = orientation === "v" ? [66, 86, 66] : [54, 70, 54];
    const order = [1, 0, 2]; // 2nd · 1st · 3rd, 1st slightly larger in the centre
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={color} show={show} />
                {show("matchup") && <span className="text-[10px] font-black uppercase tracking-widest text-white/45">{s.matchup || ""}</span>}
            </div>
            {show("title") && <div className="mb-1 mt-1 text-center"><span className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">Three Stars</span></div>}
            <div className="flex-1 flex items-center justify-center gap-4 sm:gap-6">
                {order.map((oi, slot) => {
                    const st = stars[oi]; if (!st) return null;
                    const rank = oi + 1;
                    const sc = tcol(st.subject?.team_id) || color;
                    return (
                        <div key={oi} className="flex flex-col items-center gap-2" data-testid={`game-star-${rank}`}>
                            <div className="relative">
                                {show("face") && <Face subject={st.subject} color={sc} px={px[slot]} />}
                                <span className="absolute -bottom-1 -right-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-black text-white" style={{ background: sc }}><Star className="h-2.5 w-2.5 fill-white" />{rank}</span>
                            </div>
                            <div className="text-center">
                                <div className="font-story-punch uppercase leading-none text-[13px] sm:text-sm text-white truncate max-w-[96px]">{deaccent((st.subject?.name || "")).split(/\s+/).slice(-1)[0]}</div>
                                {st.line && <div className="text-[9px] font-bold uppercase tracking-wide text-white/45 tabular-nums">{st.line}</div>}
                                {st.statLine && <div className="mt-0.5 text-[10px] font-black tabular-nums leading-none text-white" data-testid={`game-star-${rank}-stat`}>{st.statLine}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                {s.teamId && <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50"><TeamBadge teamId={s.teamId} size="xs" asLink={false} />{tabbr(s.teamId)}</span>}
                {footerExtra || <MoreLink source={s.sourceRef} color={tcol(s._homeTeam) || color} />}
            </div>}
        </Shell>
    );
}

// ---- Draft per-pick card — big pick number + prospect. Photo sits UP in the
// header so the name + blurb get the FULL card width (no cut-off text). ----
export function GameDraftCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="pointer-events-none absolute -right-5 top-0 font-story-punch leading-none select-none tabular-nums" style={{ fontSize: orientation === "v" ? 120 : 104, color: `${color}18` }}>{(s.pick || "1.01").split(".").pop()}</div>
            <div className="flex items-start justify-between gap-2 relative">
                <Pills pills={s.pills} color={color} show={show} />
                <div className="flex items-center gap-2 shrink-0">
                    {show("badge") && s.teamId && <div className="h-8 w-8"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
                    {show("face") && s.subject && <Face subject={s.subject} color={color} px={orientation === "v" ? 60 : 56} />}
                </div>
            </div>
            <div className="relative flex-1 flex flex-col justify-center min-h-0">
                {show("pickLabel") && <div className="font-story-punch uppercase text-2xl sm:text-3xl leading-none" style={{ color }}>{s.pickLabel || "1st Overall"}</div>}
                {show("line1") && <div className="font-story-serif text-white/70 text-sm mt-1.5 leading-tight">Pick {s.pick || "1.01"}</div>}
                {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl text-white leading-[0.95] mt-1">{s.subject?.name}</div>}
                {show("meta") && <div className="mt-2 flex items-center gap-2 text-[9px] font-black uppercase tracking-wider text-white/50">
                    {s.position && <span className="rounded px-1.5 py-0.5 border border-white/10">{s.position}</span>}
                    <span>{tabbr(s.teamId)}</span>
                </div>}
                {show("blurb") && s.blurb && <p className="mt-2.5 text-[12px] leading-snug text-white/60">{s.blurb}</p>}
            </div>
            {show("footer") && <div className="mt-auto pt-2.5 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">Draft Board</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} />}
            </div>}
        </Shell>
    );
}

// ---- Starting goalie — face, jersey number, stat line. ----
export function GameGoalieCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    // Text colour: team colour (default) / white / black.
    const tc = s._textColor === "white" ? "#FFFFFF" : s._textColor === "black" ? "#0A0B0B" : color;
    const statBg = s._textColor === "black" ? "rgba(255,255,255,0.88)" : s._textColor === "white" ? "rgba(255,255,255,0.12)" : `${color}14`;
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="pointer-events-none absolute -right-4 bottom-0 font-story-punch leading-none select-none tabular-nums opacity-[0.1]" style={{ fontSize: orientation === "v" ? 190 : 150, color }}>{s.number || ""}</div>
            <div className="flex items-center justify-between gap-2 relative">
                <Pills pills={s.pills} color={color} show={show} />
                {show("opp") && s.oppId && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/45">vs <TeamBadge teamId={s.oppId} size="xs" asLink={false} /></span>}
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-3 py-2">
                {show("face") && s.subject && <Face subject={s.subject} color={color} px={orientation === "v" ? 128 : 108} />}
                <div>
                    {show("line1") && <div className="font-story-serif text-white/70 text-sm">Starting In Net</div>}
                    {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl leading-none mt-0.5" style={{ color: tc }}>{deaccent(s.subject?.name)}</div>}
                    {show("stat") && (s.statLine
                        ? <div className="mt-1.5 inline-block text-[11px] font-black uppercase tracking-wide tabular-nums rounded px-2 py-1" style={{ color: tc, background: statBg }}>{s.statLine}</div>
                        : s._debut ? <div className="mt-1.5 inline-block text-[10px] font-black uppercase tracking-wider rounded px-2 py-1 text-white/45" style={{ background: "rgba(255,255,255,0.06)" }}>Season Debut</div> : null)}
                </div>
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || tname(s.teamId)}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} />}
            </div>}
        </Shell>
    );
}

// ---- Starting In Net — BOTH starters side-by-side (home vs away). ----
export function GameGoalieDuoCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const home = s.home || {}, away = s.away || {};
    const hCol = tcol(home.teamId), aCol = tcol(away.teamId);
    // Labels ("Starting In Net") + stat lines default to WHITE; owner may switch
    // to team colour or black via the Text style picker (_textColor).
    const labelCol = s._textColor === "team" ? (s.teamColor || hCol) : s._textColor === "black" ? "#0A0B0B" : "#FFFFFF";
    const statBg = s._textColor === "black" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)";
    const nameLines = (nm) => {
        const p = String(nm || "").trim().split(/\s+/);
        return p.length < 2 ? [nm || "", ""] : [p[0], p.slice(1).join(" ")];
    };
    const Side = ({ g, color }) => {
        const [first, last] = nameLines(deaccent(g.subject?.name));
        // Stat line renders STACKED: "0-1-0 · .813 SV% · 3.00 GAA" → three lines.
        const statParts = String(g.statLine || "").split("·").map((x) => x.trim()).filter(Boolean);
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 min-w-0">
                {show("face") && g.subject && <Face subject={g.subject} color={color} px={orientation === "v" ? 92 : 76} />}
                <div className="min-w-0 w-full flex flex-col items-center text-center">
                    {show("name") && <div className="font-story-punch uppercase text-lg sm:text-xl leading-[1.05] min-h-[2.1em] w-full text-center" style={{ color: labelCol }}>
                        <div className="truncate">{first}</div>
                        <div className="truncate">{last || "\u00A0"}</div>
                    </div>}
                    {show("team") && <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-white/45">
                        {g.teamId && <TeamBadge teamId={g.teamId} size="xs" asLink={false} />}{tabbr(g.teamId)}{g.number ? ` · #${g.number}` : ""}
                    </div>}
                    {show("stat") && (
                        <div className="mt-1.5 flex flex-col items-center justify-center gap-0.5 text-center min-h-[42px] leading-tight text-[10px] font-black uppercase tracking-wide tabular-nums rounded px-2 py-1 mx-auto w-full max-w-[150px]"
                            style={statParts.length ? { color: labelCol, background: statBg } : { color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.06)" }}>
                            {statParts.length
                                ? statParts.map((p, i) => <div key={i} className="whitespace-nowrap">{p}</div>)
                                : <div>Season Debut</div>}
                        </div>
                    )}
                </div>
            </div>
        );
    };
    return (
        <Shell color={s.teamColor || hCol} orientation={orientation} square={square} testId={testId}>
            {show("pills") && !!(s.pills || []).length && (
                <div className="flex items-center justify-center gap-1.5">
                    {(s.pills || []).map((p, i) => (
                        <span key={i} className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: labelCol }}>{p}</span>
                    ))}
                </div>
            )}
            <div className="relative flex-1 flex items-stretch justify-center gap-1 py-2">
                {/* AWAY goalie first (left), HOME goalie second (right) — home is the
                    card background colour, matching hockey's away @ home convention. */}
                <Side g={away} color={aCol} />
                <div className="shrink-0 flex items-center justify-center px-1">
                    <span className="font-story-punch uppercase text-xs text-white/35">VS</span>
                </div>
                <Side g={home} color={hCol} />
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: labelCol }}>Starting In Net</span>
                {footerExtra || <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{s.when || ""}</span>}
            </div>}
        </Shell>
    );
}

// ---- Milestone Reached — big career number behind a centred face. ----
export function GameMilestoneCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const live = (s.pills || []).some((p) => (p || "").toLowerCase() === "live");
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="pointer-events-none absolute -right-3 -top-4 font-story-punch leading-none select-none tabular-nums opacity-[0.09]" style={{ fontSize: orientation === "v" ? 170 : 140, color }}>{s.number || ""}</div>
            <div className="flex items-center justify-between gap-2 relative">
                <Pills pills={s.pills} color={color} show={show} />
                {show("badge") && s.teamId && <div className="h-8 w-8 shrink-0"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-3 py-2">
                {show("face") && s.subject && <Face subject={s.subject} color={color} px={orientation === "v" ? 120 : 104} />}
                <div>
                    {show("line1") && <div className="font-story-serif text-white/70 text-sm">{live ? "Milestone Reached" : "Milestone Watch"}</div>}
                    {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl leading-none mt-0.5 text-white">{s.subject?.name}</div>}
                    {show("stat") && <div className="mt-2 inline-flex items-baseline gap-2">
                        <span className="font-story-punch tabular-nums leading-none" style={{ fontSize: orientation === "v" ? 52 : 44, color }}>{s.number}</span>
                        <span className="font-story-punch uppercase text-sm text-white/70">{s.statLabel}</span>
                    </div>}
                </div>
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || tname(s.teamId)}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} />}
            </div>}
        </Shell>
    );
}

// ---- Record Broken — new all-time mark, previous value struck through. ----
export function GameRecordCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const rec = s.record || {};
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2 relative">
                <Pills pills={s.pills} color={color} show={show} />
                {show("badge") && s.teamId && <div className="h-8 w-8 shrink-0"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-2 py-2">
                {show("face") && s.subject && <Face subject={s.subject} color={color} px={orientation === "v" ? 104 : 90} />}
                {show("banner") && <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest" style={{ color, background: `${color}1a`, border: `1px solid ${color}59` }}><Trophy className="h-3 w-3" />Record Broken</div>}
                {show("line2") && <div className="font-story-punch uppercase text-xl sm:text-2xl leading-none text-white">{s.subject?.name}</div>}
                {show("value") && <div className="font-story-punch tabular-nums leading-none" style={{ fontSize: orientation === "v" ? 66 : 54, color }}>{rec.value}</div>}
                {show("value") && <div className="font-story-serif text-white/70 text-sm">{rec.name}</div>}
                {show("prev") && rec.prev && <div className="text-[10px] uppercase tracking-wide text-white/45">Previous mark <span className="line-through">{rec.prev}</span></div>}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || "All-Time"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} />}
            </div>}
        </Shell>
    );
}

// ---- PRESTIGE MILESTONE (GOLD, team-independent) — engraved-plaque luxe. ----
export function GameMilestonePrestigeCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const GOLD = "#F6C453";
    const live = (s.pills || []).some((p) => (p || "").toLowerCase() === "live");
    return (
        <Shell color={GOLD} orientation={orientation} square={square} testId={testId}
            bg="linear-gradient(165deg,#141207 0%,#0A0906 60%,#080706 100%)">
            <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-story-punch leading-none select-none tabular-nums" style={{ fontSize: orientation === "v" ? 300 : 220, color: GOLD, opacity: 0.06 }}>{s.number || ""}</div>
            <div className="pointer-events-none absolute inset-3 rounded-xl border" style={{ borderColor: `${GOLD}30` }} />
            <div className="relative flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={GOLD} show={show} />
                {show("badge") && s.teamId && <div className="h-8 w-8 shrink-0"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-3 py-2">
                {show("ribbon") && <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: GOLD, background: `${GOLD}14`, border: `1px solid ${GOLD}55` }}><Crown className="h-3 w-3" />{live ? "Milestone Reached" : "Milestone Watch"}</div>}
                {show("face") && s.subject && <Face subject={s.subject} color={GOLD} px={orientation === "v" ? 118 : 100} />}
                {show("name") && <div className="font-story-punch uppercase text-2xl sm:text-3xl leading-none text-white">{s.subject?.name}</div>}
                {show("number") && <div className="inline-flex items-baseline gap-2 mt-1">
                    <span className="font-story-punch tabular-nums leading-none" style={{ fontSize: orientation === "v" ? 76 : 60, color: GOLD, textShadow: `0 2px 30px ${GOLD}55` }}>{s.number}</span>
                    {show("statlabel") && <span className="font-story-serif uppercase text-sm text-white/70">{s.statLabel}</span>}
                </div>}
                {show("progress") && s.progress && <div className="text-[11px] font-mono-stat uppercase tracking-wide text-white/45">{s.progress}</div>}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${GOLD}22` }}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || tname(s.teamId)}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={GOLD} />}
            </div>}
        </Shell>
    );
}

// ---- PRESTIGE RECORD (PURPLE, team-independent) — trophy-case banner. ----
export function GameRecordPrestigeCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const PUR = "#9D4CDD";
    const rec = s.record || {};
    return (
        <Shell color={PUR} orientation={orientation} square={square} testId={testId}
            bg="linear-gradient(165deg,#140A1C 0%,#0B0710 60%,#080509 100%)">
            <div className="pointer-events-none absolute -left-8 -bottom-10 select-none" style={{ opacity: 0.07 }}><Trophy style={{ width: 280, height: 280, color: PUR }} /></div>
            <div className="pointer-events-none absolute inset-3 rounded-xl border" style={{ borderColor: `${PUR}30` }} />
            <div className="relative flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={PUR} show={show} />
                {show("badge") && s.teamId && <div className="h-8 w-8 shrink-0"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-2 py-2">
                {show("banner") && <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: PUR, background: `${PUR}18`, border: `1px solid ${PUR}5c` }}><Award className="h-3 w-3" />All-Time Record</div>}
                {show("face") && s.subject && <Face subject={s.subject} color={PUR} px={orientation === "v" ? 104 : 90} />}
                {show("name") && <div className="font-story-punch uppercase text-xl sm:text-2xl leading-none text-white">{s.subject?.name}</div>}
                {show("value") && <div className="font-story-punch tabular-nums leading-none" style={{ fontSize: orientation === "v" ? 72 : 58, color: PUR, textShadow: `0 2px 30px ${PUR}55` }}>{rec.value}</div>}
                {show("recordname") && <div className="font-story-serif text-white/70 text-sm">{rec.name}</div>}
                {show("prev") && rec.prev && <div className="text-[10px] uppercase tracking-wide text-white/45">Previous mark <span className="line-through">{rec.prev}</span></div>}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${PUR}22` }}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || "All-Time"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={PUR} />}
            </div>}
        </Shell>
    );
}


// ---- Goal-Differential comparison — ranked bars (works for 6/8/12 teams). ----
export function GameGDCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || "#38BDF8";
    const teams = [...(s.teams || [])].sort((a, b) => (b.gd ?? 0) - (a.gd ?? 0));
    // Reactive to team count → two sections: BEST half | WORST half.
    // 2 teams = 1|1, 4 = 2|2, 12 = 6|6 …
    const half = Math.ceil(teams.length / 2);
    const best = teams.slice(0, half);
    const worst = teams.slice(half);
    const Row = ({ t }) => {
        const gd = t.gd ?? 0; const pos = gd >= 0;
        return (
            <div className="flex items-center gap-1.5" data-testid={`gd-row-${t.id}`}>
                <div className="h-5 w-5 shrink-0"><TeamBadge teamId={t.id} size="xs" asLink={false} /></div>
                <span className="font-story-punch uppercase text-[11px] text-white/80 flex-1 truncate">{tabbr(t.id)}</span>
                <span className="font-story-punch tabular-nums text-sm shrink-0" style={{ color: pos ? "#4ADE80" : "#F87171" }}>{pos ? "+" : ""}{gd}</span>
            </div>
        );
    };
    const Col = ({ label, list, tone }) => (
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            {show("header") && <div className="text-[9px] font-black uppercase tracking-widest pb-1 mb-0.5 border-b border-white/10" style={{ color: tone }}>{label}</div>}
            {list.map((t) => <Row key={t.id} t={t} />)}
        </div>
    );
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                {show("header") && <span className="font-story-punch uppercase text-lg sm:text-xl text-white leading-none">{s.kicker || "Goal Differential"}</span>}
                {show("subtitle") && <span className="text-[10px] font-black uppercase tracking-widest text-white/40">{s.subtitle || "GD"}</span>}
            </div>
            <div className="mt-3 flex-1 flex items-start justify-center gap-4">
                <Col label="Best" list={best} tone="#4ADE80" />
                <div className="w-px self-stretch bg-white/10 shrink-0" />
                <Col label="Worst" list={worst} tone="#F87171" />
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">{s.source || "PWHL"}{s.seasonLabel ? ` · ${s.seasonLabel}` : ""}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} label="Standings" />}
            </div>}
        </Shell>
    );
}

// ---- Trade — two teams, players each side receives, swap arrow between. ----
export function GameTradeCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const a = s.teamId, b = s.oppId;
    const color = s.teamColor || tcol(a);
    const px = orientation === "v" ? 60 : 52;
    const Side = ({ id, gets, align }) => (
        <div className={`flex-1 min-w-0 flex flex-col ${align === "right" ? "items-end text-right" : "items-start text-left"} gap-2`}>
            {show("face") && <Crest id={id} px={px} style={s._crest || "ring"} />}
            {show("names") && <div className="font-story-punch uppercase text-sm text-white leading-none">{tnick(id)}</div>}
            {show("receives") && <div className="space-y-0.5">
                <div className="text-[8px] font-black uppercase tracking-widest text-white/35">Receives</div>
                {((gets && gets.length) ? gets : ["—"]).map((n, i) => <div key={i} className="font-story-serif text-white/85 text-[12px] leading-tight">{n}</div>)}
            </div>}
        </div>
    );
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                <Pills pills={s.pills} color={color} show={show} />
                <span className="text-[10px] font-black uppercase tracking-widest text-white/45">{s.when || "Trade"}</span>
            </div>
            {show("title") && <div className="mb-1 mt-1 text-center"><span className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">Trade</span></div>}
            <div className="flex-1 flex items-center gap-2 py-2">
                <Side id={a} gets={s.aGets} align="left" />
                <div className="flex flex-col items-center shrink-0"><ArrowLeftRight className="h-6 w-6" style={{ color }} /></div>
                <Side id={b} gets={s.bGets} align="right" />
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{tabbr(a)} ⇄ {tabbr(b)}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} />}
            </div>}
        </Shell>
    );
}

// ---- Flashback — WHITE theme, remembering a previous game. ----
export function GameFlashbackCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const home = s.teamId, away = s.oppId;
    const color = s.teamColor || tcol(home);
    const hs = s.score?.home, as = s.score?.away;
    const homeWon = (hs ?? 0) >= (as ?? 0);
    const vertical = orientation === "v" || orientation === "slimv";
    return (
        <article data-testid={testId} data-orientation={orientation}
            className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-black/10 p-5 sm:p-6 shadow-xl shadow-black/20 flex flex-col ${vertical ? "aspect-[4/5]" : "h-full min-h-[236px]"}`}
            style={{ background: "#F4F3EE" }}>
            <div className="flex items-center justify-between gap-2">
                {show("pills") && <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest" style={{ color, background: `${color}14`, border: `1px solid ${color}40` }}><History className="h-3 w-3" />{s.kicker || "Flashback"}</span>}
                <span className="text-[10px] font-black uppercase tracking-widest text-black/40">{s.when || ""}</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-3">
                <div className="flex items-center justify-center gap-4">
                    <div className="flex flex-col items-center gap-1.5">
                        {show("badges") && <div className="h-16 w-16"><TeamBadge teamId={away} size="xl" asLink={false} /></div>}
                        {show("score") && <div className="font-story-punch text-4xl tabular-nums" style={{ color: !homeWon ? tcol(away) : "#11111199" }}>{as}</div>}
                    </div>
                    {show("status") && <span className="font-story-punch text-base italic text-black/40">{s.status || "FINAL"}</span>}
                    <div className="flex flex-col items-center gap-1.5">
                        {show("badges") && <div className="h-16 w-16"><TeamBadge teamId={home} size="xl" asLink={false} /></div>}
                        {show("score") && <div className="font-story-punch text-4xl tabular-nums" style={{ color: homeWon ? color : "#11111199" }}>{hs}</div>}
                    </div>
                </div>
                {show("blurb") && s.blurb && <p className="text-center font-story-serif text-black/70 text-sm leading-snug max-w-[16rem]">{s.blurb}</p>}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-black/10 flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-black/50">{s.venue || `${tabbr(away)} @ ${tabbr(home)}`}</span>
                {footerExtra || <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider" style={{ color }}>More <ArrowRight className="h-3.5 w-3.5" /></span>}
            </div>}
        </article>
    );
}

// ---- Draft board (one round of N picks) — new template, boxy list layout.
export function DraftRoundCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || "#9D4CDD";
    const picks = s.picks || [];
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">{s.title || "Draft Board"}</div>
                {show("round") && <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest" style={{ background: color, color: "#0A0B0D" }}>{s.round || "Round 1"}</span>}
            </div>
            <div className="flex-1 flex flex-col gap-1.5 min-h-0 justify-center">
                {picks.map((p, i) => (
                    <div key={i} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded" style={{ background: "#111317", borderLeft: `3px solid ${tcol(p.team_id)}` }} data-testid={`draft-pick-${i + 1}`}>
                        {show("picknum") && <span className="font-story-punch text-lg tabular-nums w-6 shrink-0" style={{ color }}>{p.pick || i + 1}</span>}
                        {show("crest") && <div className="h-7 w-7 shrink-0 flex items-center justify-center"><TeamBadge teamId={p.team_id} size="sm" asLink={false} /></div>}
                        {show("name") && <span className="font-story-punch uppercase text-sm sm:text-base text-white truncate flex-1 min-w-0">{p.name}</span>}
                        {show("pos") && p.position && <span className="text-[9px] font-black uppercase tracking-wider text-white/45 shrink-0">{p.position}</span>}
                    </div>
                ))}
            </div>
            {show("footer") && <div className="mt-3 pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{s.footerLabel || "PWHL Draft"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} label="Full round" />}
            </div>}
        </Shell>
    );
}

// ---- Draft flashback (light editorial hero of a past pick) — new template.
export function DraftFlashbackCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const vertical = orientation === "v" || orientation === "slimv";
    return (
        <article data-testid={testId} data-orientation={orientation}
            className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-black/10 p-5 sm:p-6 shadow-xl shadow-black/20 flex flex-col ${vertical ? "aspect-[4/5]" : "h-full min-h-[236px]"}`}
            style={{ background: "#F4F3EE" }}>
            <div className="flex items-center justify-between gap-2">
                {show("pills") && <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest" style={{ color, background: `${color}14`, border: `1px solid ${color}40` }}><History className="h-3 w-3" />{s.kicker || "Draft Flashback"}</span>}
                {show("year") && <span className="text-[10px] font-black uppercase tracking-widest text-black/40">{s.when || s.year}</span>}
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-3 text-center">
                {show("pick") && <span className="font-story-punch text-5xl sm:text-6xl leading-none" style={{ color }}>{s.pickLabel || "1st"}</span>}
                {show("face") && s.subject && <Face subject={s.subject} color={color} px={vertical ? 92 : 84} />}
                {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl text-black leading-[0.95]">{s.subject?.name}</div>}
                {show("meta") && <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-black/50"><div className="h-5 w-5"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></div>{tabbr(s.teamId)}{s.position && <span>· {s.position}</span>}</div>}
                {show("blurb") && s.blurb && <p className="font-story-serif text-black/70 text-sm leading-snug max-w-[16rem]">{s.blurb}</p>}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-black/10 flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-black/50">{s.footerLabel || `${s.year || ""} Draft`}</span>
                {footerExtra || <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider" style={{ color }}>More <ArrowRight className="h-3.5 w-3.5" /></span>}
            </div>}
        </article>
    );
}

// ---- Standings table — new template, styled after the Standings page.
// Two live variants (game_standings, game_standings_magic) — every element is
// editable: title, stage tag, header, crests, footer + PTS-text colour, card
// background colour (with a "team #1 primary" button) and the optional Magic #.
// Clinch letter colours: z (league / 1st overall) gold, x (playoff berth) green,
// e (eliminated) red — matching the Standings-page legend defaults.
const CLINCH_COLOR = { z: "#F6C453", x: "#22C55E", e: "#FB7185" };

export function StandingsCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const rows = s.rows || [];
    // Accent = the LEADING (#1) team's colour — identical in the gallery and the
    // public feed. No manual PTS-text / background overrides.
    const accent = (rows[0] && tcol(rows[0].team_id)) || s.teamColor || "#9D4CDD";
    // Styled after the Game Recap templates: an accent icon-chip + punch title, a
    // solid stage pill, divide-y rows and a matching footer.
    const num = "text-[11px] tabular-nums text-white/70 whitespace-nowrap text-center shrink-0";
    const hd = "text-[9px] font-black uppercase tracking-widest text-white/40 whitespace-nowrap text-center shrink-0";
    // Per-column widths + row height are editable per-template (Creator tools).
    const cGP = s._colGP ?? 22, cRec = s._colRec ?? 58, cGD = s._colGD ?? 30, cPts = s._colPts ?? 26, cMag = s._colMag ?? 34, cGold = s._colGold ?? 34;
    const showMag = !hideEls.includes("magic") && s._showMag !== false;
    // GOLD (Gold Plan) column surfaces ONLY once a team is eliminated (some row
    // carries goldPts) — identical behaviour to the Standings page.
    const showGold = !hideEls.includes("gold") && rows.some((r) => r.goldPts != null);
    const topGold = showGold ? rows.reduce((mx, r) => (r.goldPts != null && r.goldPts > (mx ? mx.goldPts : -1) ? r : mx), null) : null;
    const gdColor = s._gdColor !== false; // GD +/- colour on by default; owner can turn OFF (plain)
    const cTeam = s._colTeam ?? 0; // 0 → team name flexes/truncates
    const rowPad = s._rowPadY ?? 6;
    const teamCls = cTeam ? "flex items-center gap-2 shrink-0" : "flex items-center gap-2 flex-1 min-w-0";
    const teamStyle = cTeam ? { width: cTeam } : undefined;
    const gdOf = (r) => (r.gd != null ? r.gd : (r.gf || 0) - (r.ga || 0));
    // PTS text style — same model as GD: colour on/off + bold on/off (no team
    // colours by default). Falls back to the legacy `_ptsStyle` for old configs.
    const ptsColor = s._ptsColor != null ? !!s._ptsColor : (s._ptsStyle === "colour" || s._ptsStyle === "top");
    const ptsBold = s._ptsBold != null ? !!s._ptsBold : (s._ptsStyle === "bold");
    const ptsStyleFor = (i) => ({ color: ptsColor ? accent : "#fff", fontWeight: ptsBold ? 900 : 600 });
    // Stat columns are reorderable via the Creator tools (`s._colOrder`).
    const colDef = {
        gp: { hd: "GP", w: cGP, cell: (r) => <span key="gp" className={num} style={{ width: cGP }}>{r.gp}</span> },
        rec: { hd: "RECORD", w: cRec, cell: (r) => <span key="rec" className={num} style={{ width: cRec }}>{r.w}-{(r.otw ?? 0) + (r.sow ?? 0)}-{(r.otl ?? 0) + (r.sol ?? 0)}-{r.l}</span> },
        gd: { hd: "GD", w: cGD, cell: (r) => { const gd = gdOf(r); return <span key="gd" className="text-[11px] tabular-nums whitespace-nowrap text-center shrink-0 font-bold" style={{ width: cGD, color: gdColor ? (gd > 0 ? "#34D399" : gd < 0 ? "#FB7185" : "rgba(255,255,255,0.55)") : "rgba(255,255,255,0.75)" }}>{gd > 0 ? `+${gd}` : gd}</span>; } },
        pts: { hd: "PTS", w: cPts, cell: (r, i) => <span key="pts" className={num} style={{ width: cPts, ...ptsStyleFor(i) }}>{r.pts}</span> },
        mag: { hd: "MAG", w: cMag, cell: (r) => <span key="mag" className={num} style={{ width: cMag }}>{r.magic == null ? "—" : r.magic}</span> },
        gold: { hd: "GOLD", w: cGold, cell: (r) => <span key="gold" className="text-[11px] tabular-nums whitespace-nowrap text-center shrink-0 font-bold" style={{ width: cGold, color: r === topGold ? "#F6C453" : (r.goldPts == null ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.75)") }}>{r.goldPts == null ? "—" : r.goldPts}</span> },
    };
    const defOrder = ["gp", "rec", "gd", "pts", ...(showMag ? ["mag"] : []), ...(showGold ? ["gold"] : [])];
    let order = (Array.isArray(s._colOrder) && s._colOrder.length ? s._colOrder : defOrder).filter((k) => colDef[k] && (k !== "mag" || showMag) && (k !== "gold" || showGold));
    defOrder.forEach((k) => { if (!order.includes(k)) order.push(k); });
    return (
        <Shell color={accent} orientation={orientation} square={square} testId={testId}>
            {/* NEW design (Standings Update style): a slim accent kicker chip — no
                symbol, no headline. */}
            {show("title") && <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full px-2.5 py-1" style={{ background: `${accent}1f`, border: `1px solid ${accent}59` }}>
                    <span className="font-story-punch uppercase text-[13px] tracking-[0.16em] leading-none" style={{ color: accent }}>{s.title || "Standings"}</span>
                </span>
                {show("stage") && <span className="ml-auto px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full whitespace-nowrap text-white/55 border border-white/15">{s.stage || "Regular Season"}</span>}
            </div>}
            <div className="flex-1 min-h-0 flex flex-col justify-center">
                {show("header") && <div className="flex items-center justify-between gap-3 px-2.5 pb-1">
                    <div className={cTeam ? "flex items-center gap-2 shrink-0" : "flex items-center gap-2 flex-1 min-w-0"} style={teamStyle}><span className={`${hd} w-4 text-left`}>#</span><span className={`${hd} text-left`}>Team</span></div>
                    <div className="flex items-center gap-3 shrink-0">
                        {order.map((k) => <span key={k} className={hd} style={{ width: colDef[k].w }}>{colDef[k].hd}</span>)}
                    </div>
                </div>}
                <div className="flex flex-col gap-1">
                    {rows.map((r, i) => {
                        const rc = tcol(r.team_id);
                        return (
                            <div key={i} className="relative flex items-center justify-between gap-3 rounded-lg pl-3 pr-2.5 overflow-hidden bg-white/[0.04]" style={{ paddingTop: rowPad, paddingBottom: rowPad }} data-testid={`standings-row-${i + 1}`}>
                                <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg" style={{ background: rc }} />
                                <span className="absolute left-0 top-0 bottom-0 w-full pointer-events-none" style={{ background: `linear-gradient(90deg, ${rc}14, transparent 42%)` }} />
                                <div className={teamCls} style={teamStyle}>
                                    <span className="relative font-story-punch text-sm tabular-nums text-white/55 w-4 shrink-0">{r.rank || i + 1}</span>
                                    {show("crest") && <div className="relative h-5 w-5 shrink-0 flex items-center justify-center"><TeamBadge teamId={r.team_id} size="xs" asLink={false} /></div>}
                                    <span className="relative font-story-punch uppercase text-xs sm:text-sm text-white truncate min-w-0" data-testid={r.clinch ? `standings-clinch-${r.clinch}` : undefined}>{r.clinch ? `${r.clinch}-` : ""}{tabbr(r.team_id)}</span>
                                </div>
                                <div className="relative flex items-center gap-3 shrink-0">
                                    {order.map((k) => colDef[k].cell(r, i))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {show("footer") && <div className="mt-3 pt-3 border-t border-white/[0.07] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{s.footerLabel || "PWHL Standings"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={accent} label="Full table" />}
            </div>}
        </Shell>
    );
}

// ---- Play-by-play cards (goal / shot / faceoff / penalty / goalie) --------
// Built from REAL game-centre play-by-play events (pxp). Two variants each:
// "spotlight" (vertical hero) and "ticker" (compact strip). Every element is
// toggleable. `s._pbpKind` picks the label/accent; `s._pbpVariant` the design.
const PBP_META = {
    goal: { label: "Goal", accent: "#F5C518" },
    shot: { label: "Shot on Goal", accent: "#38BDF8" },
    faceoff: { label: "Faceoff Won", accent: "#34D399" },
    penalty: { label: "Penalty", accent: "#FB7185" },
    goalie: { label: "Goaltender", accent: "#A78BFA" },
    hit: { label: "Hit", accent: "#F97316" },
    block: { label: "Blocked Shot", accent: "#22D3EE" },
    shootout: { label: "Shootout", accent: "#F5C518" },
};
export function PbpCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const kind = s._pbpKind || "goal";
    const meta = PBP_META[kind] || PBP_META.goal;
    // Penalties are ALWAYS red — never team-coloured.
    const accent = kind === "penalty" ? "#EF4444" : (s.teamColor || meta.accent);
    const variant = s._pbpVariant || "spotlight";
    const clock = `${s.period ? `P${s.period}` : ""}${s.period && s.time ? " · " : ""}${s.time || ""}`.trim();
    const TimeBadge = () => show("time") && !!clock && (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider tabular-nums" style={{ color: accent, background: `${accent}1a`, border: `1px solid ${accent}59` }}>{clock}</span>
    );

    if (variant === "ticker") {
        const spos = s._subjectPos || "center";
        const alignCls = spos === "top" ? "items-start" : spos === "bottom" ? "items-end" : "items-center";
        return (
            <Shell color={accent} orientation={orientation} square={square} testId={testId}>
                <div className="flex items-center justify-between gap-2">
                    {show("label") && <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest" style={{ background: accent, color: "#0A0B0D" }}>{s.label || meta.label}</span>}
                    <div className="flex items-center gap-2"><TimeBadge />{show("crest") && s.teamId && <div className="h-6 w-6"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}</div>
                </div>
                <div className={`flex-1 min-h-0 flex ${alignCls}`}>
                    <div className="flex items-center gap-3 min-w-0 w-full">
                        {show("face") && s.subject && <Face subject={s.subject} color={accent} px={orientation === "v" || orientation === "slimv" ? 62 : 54} />}
                        <div className="min-w-0">
                            {show("name") && <div className="font-story-punch uppercase text-lg sm:text-xl text-white leading-none truncate">{deaccent(s.subject?.name) || meta.label}</div>}
                            {show("detail") && s.detail && <div className="mt-1 text-[12px] text-white/60 leading-snug line-clamp-2">{deaccent(s.detail)}</div>}
                        </div>
                    </div>
                </div>
                {show("footer") && <div className="mt-3 pt-3 border-t border-white/[0.07] flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId ? tnick(s.teamId) : "PWHL"} · Play-by-play</span>
                    {footerExtra || (s._gcMode
                        ? <MoreLink source={s.sourceRef} color={accent} label="Stories" href={s._storiesHref} />
                        : <MoreLink source={s.sourceRef} color={accent} label="Game centre" href={s.moreHref} />)}
                </div>}
            </Shell>
        );
    }
    // spotlight (default)
    return (
        <Shell color={accent} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2">
                <Pills pills={s.pills || [meta.label]} color={accent} show={show} />
                <TimeBadge />
            </div>
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center gap-2">
                {show("face") && s.subject && <Face subject={s.subject} color={accent} px={orientation === "v" || orientation === "slimv" ? 104 : 84} />}
                {show("label") && <div className="font-story-punch uppercase text-2xl sm:text-3xl leading-none" style={{ color: accent }}>{s.label || meta.label}</div>}
                {show("name") && <div className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">{deaccent(s.subject?.name)}</div>}
                {show("detail") && s.detail && <div className="text-[12.5px] text-white/60 leading-snug max-w-[92%]">{deaccent(s.detail)}</div>}
            </div>
            {show("footer") && <div className="mt-3 pt-3 border-t border-white/[0.07] flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}{s.teamId ? tnick(s.teamId) : "PWHL"} · Play-by-play</span>
                {footerExtra || (s._gcMode
                    ? <MoreLink source={s.sourceRef} color={accent} label="Stories" href={s._storiesHref} />
                    : <MoreLink source={s.sourceRef} color={accent} label="Game centre" href={s.moreHref} />)}
            </div>}
        </Shell>
    );
}

// ---- Miscellaneous: Happy Birthday — festive, NEVER team-coloured ---------
export function BirthdayCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const PINK = "#F472B6";
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={PINK} orientation={orientation} square={square} testId={testId}
            bg="radial-gradient(120% 85% at 50% 0%, rgba(244,114,182,0.16), rgba(10,11,13,0) 58%), linear-gradient(180deg,#111014,#0A090C)">
            <div className="relative flex items-center justify-between gap-2">
                {show("pills") && <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: PINK, background: `${PINK}18`, border: `1px solid ${PINK}55` }}>{s.pills?.[0] || "Happy Birthday"}</span>}
                {show("date") && s.dateLabel && <span className="text-[10px] font-black uppercase tracking-widest text-white/45">{s.dateLabel}</span>}
            </div>
            <div className="relative flex-1 min-h-0 flex flex-col items-center justify-center text-center gap-3">
                {show("face") && s.subject && <Face subject={s.subject} color={PINK} px={big ? 116 : 90} />}
                <div>
                    {show("label") && <div className="font-heading font-black uppercase tracking-[0.16em] text-sm" style={{ color: PINK }}>Happy Birthday</div>}
                    {show("name") && <div className="mt-1 font-story-punch uppercase text-white leading-[0.9]" style={{ fontSize: big ? 34 : 28 }}>{s.subject?.name || ""}</div>}
                    {show("age") && s.age != null && <div className="mt-1.5 text-[11px] font-black uppercase tracking-wider text-white/55">Turns {s.age} Today</div>}
                </div>
            </div>
            {show("footer") && <div className="relative mt-3 pt-3 border-t border-white/[0.07] flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider" style={{ color: PINK }}><Cake className="h-3.5 w-3.5" />Rosterly</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={PINK} label="Player" />}
            </div>}
        </Shell>
    );
}

// ---- Lineup unit (lines / pairs) — for the 3-slide Lineup story ----------
export function LineupUnitCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const accent = s.teamColor || "#9D4CDD";
    const units = s.units || [];
    const px = 34;
    return (
        <Shell color={accent} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
                {show("title") && <div className="font-story-punch uppercase text-lg text-white leading-none">{s.title || "Lineup"}</div>}
                {show("crest") && s.teamId && <div className="h-6 w-6"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>}
            </div>
            <div className="flex-1 min-h-0 flex flex-col justify-center gap-1.5">
                {units.map((u, i) => (
                    <div key={i} className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-2 py-1.5">
                        {show("label") && <div className="text-[8px] font-black uppercase tracking-widest mb-1" style={{ color: accent }}>{u.label}</div>}
                        <div className="flex items-center justify-between gap-1">
                            {(u.players || []).map((p, j) => (
                                <div key={j} className="flex items-center gap-1.5 min-w-0 flex-1">
                                    {show("face") && <Face subject={{ name: p.name, photo_url: p.photo_url || null, id: p.id || "" }} color={accent} px={px} />}
                                    {show("name") && <span className="font-story-punch uppercase text-[9px] text-white leading-tight truncate">{(p.name || "").split(/\s+/).slice(-1)[0]}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {show("footer") && <div className="mt-1.5 pt-1.5 border-t border-white/[0.07] flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <span className="h-3.5 w-3.5"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}{s.teamId ? tnick(s.teamId) : "PWHL"} · {s.footerLabel || "Lineup"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={accent} label="Full lineup" />}
            </div>}
        </Shell>
    );
}

// ---- Next Game — shows the AWAY team's next opponent and the HOME team's next
// opponent (two rows). Gallery + live share this component + the same live
// `schedule`, so both always match. ------------------------------------------
export function NextGameCard({ s, orientation = "v", square = false, hideEls = [], testId, schedule = [], footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    // Per-game emission passes `_nextRows` (the next game for EACH team that
    // played this game). Fall back to a single computed/sample matchup for the
    // Creator preview. ALWAYS rendered away → home to match the other cards.
    let rows = Array.isArray(s._nextRows) && s._nextRows.length ? s._nextRows : null;
    if (!rows) {
        let away = s._nextAway, home = s._nextHome, dateLabel = s._nextDate;
        if (!home) {
            const homeId = s._homeId || "tor", awayId = s._awayId || "mtl";
            const after = s._afterDate || todayStr;
            const nx = (schedule || [])
                .filter((g) => [g.home_team_id, g.away_team_id].some((t) => t === homeId || t === awayId) && dOnly(g.date) > after)
                .sort((a, b) => dOnly(a.date).localeCompare(dOnly(b.date)))[0];
            if (nx) { away = nx.away_team_id; home = nx.home_team_id; dateLabel = new Date(dOnly(nx.date) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
            else { away = awayId; home = homeId; dateLabel = "Jan 12"; }
        }
        rows = [{ a: away, h: home, date: dateLabel }];
    }
    const color = s.teamColor || tcol(rows[0].h);
    const lg = s._todayLogo ?? 30, padY = s._todayPadY ?? 8;
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            {show("header") && <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0" style={{ background: `${color}1f`, color }}><CalendarDays className="h-3.5 w-3.5" /></span>
                <span className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">Next Game</span>
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded whitespace-nowrap" style={{ background: color, color: "#0A0B0D" }}>Next Up</span>
            </div>}
            <div className="mt-2 flex-1 min-h-0 flex flex-col justify-center divide-y divide-white/[0.06]">
                {rows.map((g, i) => <MatchupRow key={i} a={g.a} h={g.h} center={g.date || "@"} color={color} lg={lg} padY={padY} testId={i === 0 ? "next-game-row" : `next-game-row-${i}`} />)}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tabbr(rows[0].a)} @ {tabbr(rows[0].h)} · Next up</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} label="Schedule" />}
            </div>}
        </Shell>
    );
}

// End-of-game-day slate — every team that played that day, its next game, in one
// card. Deduped upstream so a rematch (same 2 teams) appears only once.
export function NextGamesSlateCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || "#38BDF8";
    const rows = (s._slate && s._slate.length) ? s._slate : [{ a: "mtl", h: "tor", date: "Jan 12" }, { a: "ott", h: "bos", date: "Jan 12" }];
    const lg = s._todayLogo ?? 28, padY = s._todayPadY ?? 8;
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            {show("header") && <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0" style={{ background: `${color}1f`, color }}><CalendarDays className="h-3.5 w-3.5" /></span>
                <span className="font-story-punch uppercase text-xl sm:text-2xl text-white leading-none">Next Games</span>
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded whitespace-nowrap" style={{ background: color, color: "#0A0B0D" }}>Upcoming</span>
                {show("count") && <span className="text-[9px] font-black uppercase tracking-widest text-white/45 tabular-nums whitespace-nowrap">{rows.length} {rows.length === 1 ? "game" : "games"}</span>}
            </div>}
            <div className="mt-2 flex-1 min-h-0 overflow-y-auto flex flex-col justify-start divide-y divide-white/[0.06]">
                {rows.map((g, i) => <MatchupRow key={i} a={g.a} h={g.h} center={g.date || "@"} color={color} lg={lg} padY={padY} testId={`next-slate-row-${i}`} />)}
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/45">{s.source || "PWHL"} · Next up</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={color} label="Full schedule" />}
            </div>}
        </Shell>
    );
}

// ---- Award · Feature (16:9) — the hero award cards on the Awards page.
// Big faded jersey number, accent top-rail, player face, award + name. ----
export function GameAwardCard({ s, orientation = "h", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const accent = s.accent || s.teamColor || tcol(s.teamId);
    const vertical = orientation === "v" || orientation === "slimv";
    const bg = { background: `radial-gradient(130% 150% at 82% 8%, ${accent}30 0%, transparent 55%), linear-gradient(160deg, #101114 0%, #08090B 100%)` };
    const px = vertical ? 118 : 104;
    const num = s.number;
    const hasNum = num != null && num !== "";
    return (
        <article data-testid={testId} data-orientation={orientation}
            className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 hover:border-white/20 transition-colors p-5 sm:p-6 shadow-xl shadow-black/40 flex flex-col ${vertical ? "aspect-[4/5]" : "h-full min-h-[220px]"}`}
            style={{ ...bg, borderTop: `3px solid ${accent}` }}>
            {show("number") && hasNum && (
                <div className="pointer-events-none absolute -right-5 -bottom-10 font-story-punch leading-none select-none tabular-nums opacity-[0.11]" style={{ fontSize: vertical ? 220 : 190, color: accent }}>{num}</div>
            )}
            <div className="relative flex items-center justify-between gap-2">
                {show("award") && <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-black uppercase tracking-widest leading-tight" style={{ color: accent }}><Trophy className="h-3.5 w-3.5 shrink-0" />{s.award || "Award"}{s.featured && <Star className="h-3 w-3 fill-current shrink-0" />}</span>}
                {show("season") && s.season && <span className="text-[10px] font-black uppercase tracking-widest text-white/40 tabular-nums shrink-0">{s.season}</span>}
            </div>
            <div className={`relative flex-1 min-h-0 flex ${vertical ? "flex-col items-center text-center justify-center gap-3" : "items-center gap-4"} py-2`}>
                {show("face") && s.subject && <Face subject={s.subject} color={accent} px={px} />}
                <div className={`min-w-0 ${vertical ? "" : "flex-1"}`}>
                    {show("name") && <div className="font-story-punch uppercase leading-[0.9] text-white text-2xl sm:text-4xl">{s.subject?.name}</div>}
                    {hasNum && <div className="mt-1 font-story-serif text-white/55 text-sm">#{num}{s.subject?.team_id ? ` · ${tabbr(s.subject.team_id)}` : ""}</div>}
                </div>
            </div>
            {show("footer") && <div className="relative mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{tname(s.teamId)}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={accent} label="Details" />}
            </div>}
        </article>
    );
}


// ═══════════════════ V1 NEWS TEMPLATES ═══════════════════
// A cleaner, information-first news/transaction card. No pills/tags — just the
// eyebrow, the name, the story and a footer. Team-COLOUR driven (never gold),
// with the team crest as a big background watermark and the player face inset.
export function V1NewsCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const eyebrow = s.line1 || (s.type ? String(s.type).replace(/_/g, " ").toUpperCase() : "NEWS");
    const name = (s.subject?.name || s.line2 || "").toUpperCase();
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`linear-gradient(158deg, ${tc}1f 0%, #0A0A0C 52%, #060607 100%)`}>
            {show("logo") && s.teamId && (
                <div className="pointer-events-none absolute -left-10 bottom-[-56px] select-none" style={{ opacity: 0.12, transform: "scale(3.4)", transformOrigin: "left bottom" }}>
                    <TeamBadge teamId={s.teamId} size="xl" asLink={false} />
                </div>
            )}
            <div className="pointer-events-none absolute inset-0" style={{ background: `linear-gradient(0deg, ${tc}10, transparent 40%)` }} />
            {show("face") && s.subject && (
                <div className="absolute right-4 top-4 z-10"><Face subject={s.subject} color={tc} px={big ? 104 : 84} /></div>
            )}
            <div className="relative z-10 flex-1 flex flex-col justify-end pr-2">
                {show("eyebrow") && <div className="font-story-serif uppercase tracking-[0.16em] mb-1.5" style={{ color: tc, fontSize: big ? 14 : 12 }}>{eyebrow}</div>}
                {show("name") && <div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 46 : 36, lineHeight: 0.92 }}>{name}</div>}
                {show("blurb") && s.blurb && <p className="mt-3 leading-snug text-white/70 max-w-[94%]" style={{ fontSize: big ? 13 : 12 }}>{s.blurb}</p>}
            </div>
            {show("footer") && (
                <div className="relative z-10 mt-4 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${tc}33` }}>
                    <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        {tabbr(s.teamId)}{(s.when || s.ago) ? ` · ${s.when || s.ago}` : ""}
                    </span>
                    {footerExtra || <MoreLink source={s.sourceRef} color={tc} />}
                </div>
            )}
        </Shell>
    );
}

// ═══════════════════ V1 — MILESTONES (5 distinct, simple, prestige) ═══════════
// All team-colour driven and info-first, matching the V1 news/signing card
// prestige + simplicity. Each design is DELIBERATELY unlike the others.
// Shared story shape: { teamId, when, jersey, blurb, footerLabel,
//   milestone: { kicker, value, name, prev }, subject }.

const splitName = (n) => {
    const parts = String(n || "").trim().split(/\s+/);
    const last = parts.length > 1 ? parts.pop() : (parts[0] || "");
    return { first: parts.join(" "), last };
};

// (1) MARQUEE — crest watermark + player circle + a bold stat CHIP for the
// number. The information-first sibling of the V1 signing card.
export function V1MilestoneMarqueeCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const { first, last } = splitName(s.subject?.name);
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`linear-gradient(158deg, ${tc}22 0%, #0A0A0C 52%, #060607 100%)`}>
            {show("logo") && s.teamId && (
                <div className="pointer-events-none absolute -left-10 bottom-[-56px] select-none" style={{ opacity: 0.12, transform: "scale(3.4)", transformOrigin: "left bottom" }}>
                    <TeamBadge teamId={s.teamId} size="xl" asLink={false} />
                </div>
            )}
            {show("face") && s.subject && <div className="absolute right-4 top-4 z-10"><Face subject={s.subject} color={tc} px={big ? 100 : 82} /></div>}
            <div className="relative z-10 flex-1 flex flex-col justify-end pr-2">
                {show("eyebrow") && <div className="font-story-serif uppercase tracking-[0.16em] mb-1.5" style={{ color: tc, fontSize: big ? 14 : 12 }}>{ms.kicker || "Career Milestone"}</div>}
                {show("name") && <div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 42 : 34, lineHeight: 0.9 }}>{first && <div className="text-white/80" style={{ fontSize: big ? 18 : 15 }}>{first}</div>}{last}</div>}
                {show("stat") && <div className="mt-3 inline-flex items-center gap-3 self-start rounded-lg px-3 py-2" style={{ background: `${tc}1c`, border: `1px solid ${tc}55` }}>
                    <span className="font-story-punch tabular-nums leading-none" style={{ color: tc, fontSize: big ? 44 : 36 }}>{ms.value}</span>
                    <span className="text-[11px] font-black uppercase tracking-wider text-white/70 leading-tight">{ms.name}</span>
                </div>}
                {show("blurb") && s.blurb && <p className="mt-3 leading-snug text-white/70 max-w-[92%]" style={{ fontSize: big ? 12 : 11 }}>{s.blurb}</p>}
            </div>
            {show("footer") && (
                <div className="relative z-10 mt-4 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${tc}33` }}>
                    <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        {tabbr(s.teamId)}{(s.when) ? ` · ${s.when}` : ""}
                    </span>
                    {footerExtra || <MoreLink source={s.sourceRef} color={tc} />}
                </div>
            )}
        </Shell>
    );
}

// (2) BIG NUMBER — the number IS the poster. Massive solid team-colour numeral,
// tiny supporting text. Maximum impact, minimum clutter.
export function V1MilestoneNumberCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`radial-gradient(120% 90% at 50% 40%, ${tc}22 0%, #08080A 60%, #060607 100%)`}>
            <div className="relative z-10 flex items-center justify-between">
                {show("eyebrow") && <span className="font-story-serif uppercase tracking-[0.22em]" style={{ color: tc, fontSize: big ? 13 : 11 }}>{ms.kicker || "Milestone"}</span>}
                {show("date") && <span className="font-mono-stat text-[10px] uppercase tracking-[0.22em] text-white/50">{s.when || ""}</span>}
            </div>
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
                {show("number") && <div className="font-story-punch tabular-nums leading-[0.8] text-center" style={{ color: tc, fontSize: big ? 200 : 150, textShadow: `0 0 40px ${tc}55` }}>{ms.value}</div>}
                {show("stat") && <div className="mt-1 font-story-punch uppercase tracking-[0.12em] text-white text-center" style={{ fontSize: big ? 20 : 16 }}>{ms.name}</div>}
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center justify-center gap-2.5">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 44 : 38} />}
                    <div className="text-center leading-tight">
                        <div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 22 : 18 }}>{s.subject?.name}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div>
                    </div>
                </div>
            )}
        </Shell>
    );
}

// (3) TICKET — a two-part pass split by a dashed perforation; the number lives
// in the stub. Reads like a commemorative admission ticket.
export function V1MilestoneTicketCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`linear-gradient(160deg, #0C0C0F 0%, #08080A 100%)`}>
            <div className="relative z-10 flex-1 flex flex-col rounded-xl overflow-hidden" style={{ border: `1px solid ${tc}44` }}>
                <div className="flex items-center justify-between px-3 py-2" style={{ background: `${tc}1e`, borderBottom: `1px dashed ${tc}55` }}>
                    {show("eyebrow") && <span className="inline-flex items-center gap-1.5 font-story-serif uppercase tracking-[0.16em]" style={{ color: tc, fontSize: big ? 12 : 11 }}><Medal className="h-3.5 w-3.5" />{ms.kicker || "Milestone Pass"}</span>}
                    {show("date") && <span className="font-mono-stat text-[9px] uppercase tracking-[0.18em] text-white/50">{s.when || ""}</span>}
                </div>
                <div className="flex flex-1">
                    <div className="flex-1 flex flex-col justify-center p-4 min-w-0">
                        {show("face") && s.subject && <div className="mb-3"><Face subject={s.subject} color={tc} px={big ? 60 : 50} /></div>}
                        {show("name") && <div className="font-story-punch uppercase text-white leading-[0.9]" style={{ fontSize: big ? 30 : 24 }}>{s.subject?.name}</div>}
                        {show("stat") && <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-white/55">{ms.name}</div>}
                        {show("blurb") && s.blurb && <p className="mt-2 leading-snug text-white/55" style={{ fontSize: big ? 11 : 10 }}>{s.blurb}</p>}
                    </div>
                    <div className="flex flex-col items-center justify-center px-4 shrink-0" style={{ borderLeft: `1px dashed ${tc}55`, background: `${tc}12` }}>
                        {show("number") && <span className="font-story-punch tabular-nums leading-none" style={{ color: tc, fontSize: big ? 80 : 60 }}>{ms.value}</span>}
                        {show("footer") && <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/60">{s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}{tabbr(s.teamId)}</span>}
                    </div>
                </div>
            </div>
        </Shell>
    );
}

// (4) TIMELINE — the journey to the milestone shown as a dotted progression,
// current mark enlarged. Emphasises the climb, not just the number.
export function V1MilestoneTimelineCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const steps = Array.isArray(ms.steps) && ms.steps.length ? ms.steps : [ms.prev, ms.value].filter(Boolean);
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`linear-gradient(158deg, ${tc}1c 0%, #0A0A0C 55%, #060607 100%)`}>
            <div className="relative z-10 flex items-center gap-3">
                {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 64 : 54} />}
                <div className="min-w-0">
                    {show("eyebrow") && <div className="font-story-serif uppercase tracking-[0.16em]" style={{ color: tc, fontSize: big ? 12 : 11 }}>{ms.kicker || "Career Milestone"}</div>}
                    {show("name") && <div className="font-story-punch uppercase text-white leading-[0.9]" style={{ fontSize: big ? 30 : 24 }}>{s.subject?.name}</div>}
                    {show("stat") && <div className="text-[11px] font-bold uppercase tracking-wider text-white/55">{ms.name}</div>}
                </div>
            </div>
            {show("timeline") && (
                <div className="relative z-10 mt-auto mb-2">
                    <div className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2" style={{ background: `${tc}33` }} />
                    <div className="relative flex items-center justify-between">
                        {steps.map((v, i) => {
                            const cur = i === steps.length - 1;
                            return (
                                <div key={i} className="flex flex-col items-center gap-1.5">
                                    <span className="rounded-full flex items-center justify-center font-story-punch tabular-nums"
                                        style={cur
                                            ? { width: big ? 72 : 58, height: big ? 72 : 58, background: tc, color: "#0A0A0C", fontSize: big ? 26 : 20, boxShadow: `0 0 24px ${tc}66` }
                                            : { width: big ? 40 : 34, height: big ? 40 : 34, background: "#141418", border: `2px solid ${tc}66`, color: "#ffffffb0", fontSize: big ? 15 : 13 }}>{v}</span>
                                    <span className="text-[8px] font-black uppercase tracking-wider" style={{ color: cur ? tc : "rgba(255,255,255,0.35)" }}>{cur ? "Now" : ""}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {show("footer") && (
                <div className="relative z-10 mt-2 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: `${tc}33` }}>
                    <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        {tabbr(s.teamId)}{(s.when) ? ` · ${s.when}` : ""}
                    </span>
                    {footerExtra || <MoreLink source={s.sourceRef} color={tc} />}
                </div>
            )}
        </Shell>
    );
}

// (5) MEDALLION — a centred commemorative seal. The number sits inside a
// ringed medal, player face above, everything symmetric. Trophy-case feel.
export function V1MilestoneMedallionCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    const d = big ? 150 : 118;
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId}
            bg={`radial-gradient(110% 80% at 50% 30%, ${tc}20 0%, #08080A 62%, #060607 100%)`}>
            {show("eyebrow") && (
                <div className="relative z-10 mx-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 self-center" style={{ color: tc, background: `${tc}18`, border: `1px solid ${tc}55` }}>
                    <Crown className="h-3 w-3" /><span className="text-[10px] font-black uppercase tracking-[0.2em]">{ms.kicker || "Career Milestone"}</span>
                </div>
            )}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-3">
                {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 60 : 50} />}
                {show("number") && (
                    <div className="relative flex items-center justify-center rounded-full" style={{ width: d, height: d, border: `3px solid ${tc}`, boxShadow: `0 0 30px ${tc}44, inset 0 0 24px ${tc}22` }}>
                        <div className="absolute inset-1.5 rounded-full border" style={{ borderColor: `${tc}44` }} />
                        <span className="font-story-punch tabular-nums leading-none" style={{ color: tc, fontSize: big ? 58 : 46 }}>{ms.value}</span>
                    </div>
                )}
                {show("stat") && <div className="font-story-punch uppercase tracking-[0.1em] text-white text-center" style={{ fontSize: big ? 17 : 14 }}>{ms.name}</div>}
                {show("name") && <div className="text-center leading-tight -mt-1">
                    <div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 24 : 19 }}>{s.subject?.name}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div>
                </div>}
            </div>
            {show("footer") && (
                <div className="relative z-10 pt-3 border-t flex items-center justify-center gap-2" style={{ borderColor: `${tc}33` }}>
                    <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/55">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        {s.footerLabel || "PWHL Milestones"}{(s.when) ? ` · ${s.when}` : ""}
                    </span>
                </div>
            )}
        </Shell>
    );
}

// ═══════════════ V1 MILESTONES — 10 additional bespoke designs ═══════════════
// Shared footer strip (kept tiny so each card reads uniquely above it).
function MileFoot({ tc, s, center = false, label, footerExtra }) {
    return (
        <div className={`relative z-10 mt-auto pt-3 border-t flex items-center gap-2 ${center ? "justify-center" : "justify-between"}`} style={{ borderColor: `${tc}33` }}>
            <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.16em] text-white/55">
                {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                {label || s.footerLabel || tabbr(s.teamId)}{(s.when) ? ` · ${s.when}` : ""}
            </span>
            {!center && (footerExtra || <MoreLink source={s.sourceRef} color={tc} />)}
        </div>
    );
}

// (6) STAMP — a rotated distressed rubber-stamp seal; the number is the ink.
export function V1MilestoneStampCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(160deg,#0D0D10 0%,#08080A 100%)">
            {show("eyebrow") && <div className="relative z-10 flex items-center gap-2" style={{ color: tc }}><Award className="h-4 w-4" /><span className="font-story-serif uppercase tracking-[0.2em]" style={{ fontSize: big ? 13 : 11 }}>{ms.kicker || "Officially Reached"}</span></div>}
            <div className="relative z-10 flex-1 flex items-center justify-center">
                {show("number") && (
                    <div className="relative flex flex-col items-center justify-center rounded-full text-center"
                        style={{ width: big ? 190 : 150, height: big ? 190 : 150, border: `4px double ${tc}`, transform: "rotate(-11deg)", boxShadow: `inset 0 0 0 6px ${tc}18`, color: tc, mixBlendMode: "screen", opacity: 0.95 }}>
                        <span className="text-[9px] font-black uppercase tracking-[0.34em] mb-1">Milestone</span>
                        <span className="font-story-punch tabular-nums leading-none" style={{ fontSize: big ? 74 : 58 }}>{ms.value}</span>
                        {show("stat") && <span className="text-[10px] font-black uppercase tracking-[0.18em] mt-1 px-3 leading-tight">{ms.name}</span>}
                    </div>
                )}
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center gap-2.5 mb-3">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 46 : 40} />}
                    <div className="leading-tight"><div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 24 : 19 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div></div>
                </div>
            )}
            {show("footer") && <MileFoot tc={tc} s={s} footerExtra={footerExtra} />}
        </Shell>
    );
}

// (7) NEON — arcade scoreboard: glowing outlined numeral over a scanline grid.
export function V1MilestoneNeonCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(180deg,#070A12 0%,#05060A 100%)">
            <div className="pointer-events-none absolute inset-0 opacity-[0.18]" style={{ backgroundImage: `repeating-linear-gradient(0deg, ${tc}00 0px, ${tc}00 3px, ${tc} 4px)` }} />
            <div className="relative z-10 flex items-center justify-between">
                {show("eyebrow") && <span className="font-mono-stat uppercase tracking-[0.28em]" style={{ color: tc, fontSize: big ? 12 : 10, textShadow: `0 0 12px ${tc}` }}>{ms.kicker || "◆ Milestone ◆"}</span>}
                {show("date") && <span className="font-mono-stat text-[10px] tracking-[0.2em] text-white/40">{s.when || ""}</span>}
            </div>
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
                {show("number") && <div className="font-story-punch tabular-nums leading-[0.8]" style={{ fontSize: big ? 172 : 128, color: "transparent", WebkitTextStroke: `3px ${tc}`, textShadow: `0 0 26px ${tc}aa, 0 0 60px ${tc}66` }}>{ms.value}</div>}
                {show("stat") && <div className="mt-2 font-mono-stat uppercase tracking-[0.34em] text-white/80" style={{ fontSize: big ? 15 : 12 }}>{ms.name}</div>}
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center justify-center gap-2.5 mb-3">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 42 : 36} />}
                    <div className="font-story-punch uppercase text-white text-center" style={{ fontSize: big ? 22 : 18, textShadow: `0 0 14px ${tc}55` }}>{s.subject?.name}</div>
                </div>
            )}
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}

// (8) SPLIT — hard diagonal: solid team-colour face panel vs black number panel.
export function V1MilestoneSplitCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(160deg,#0B0B0E 0%,#070708 100%)">
            <div className="absolute inset-0" style={{ background: tc, clipPath: "polygon(0 0, 62% 0, 38% 100%, 0 100%)", opacity: 0.9 }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${tc}00 40%, #000 40%)`, opacity: 0.14 }} />
            <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="flex items-start justify-between">
                    {show("face") && s.subject && <Face subject={s.subject} color="#0A0A0C" px={big ? 96 : 78} />}
                    {show("number") && <div className="text-right"><div className="font-story-punch tabular-nums leading-[0.8] text-white" style={{ fontSize: big ? 108 : 82 }}>{ms.value}</div>{show("stat") && <div className="text-[11px] font-black uppercase tracking-wider" style={{ color: tc }}>{ms.name}</div>}</div>}
                </div>
                <div>
                    {show("eyebrow") && <div className="font-story-serif uppercase tracking-[0.16em] text-white/90" style={{ fontSize: big ? 13 : 11 }}>{ms.kicker || "Career Milestone"}</div>}
                    {show("name") && <div className="font-story-punch uppercase text-white leading-[0.88]" style={{ fontSize: big ? 40 : 32 }}>{s.subject?.name}</div>}
                    {show("blurb") && s.blurb && <p className="mt-2 leading-snug text-white/60 max-w-[90%]" style={{ fontSize: big ? 12 : 11 }}>{s.blurb}</p>}
                </div>
                {show("footer") && <MileFoot tc={tc} s={s} footerExtra={footerExtra} />}
            </div>
        </Shell>
    );
}

// (9) BANNER — a rafters championship banner; the number hangs at its point.
export function V1MilestoneBannerCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="radial-gradient(120% 80% at 50% -10%, #16161c 0%, #08080A 60%)">
            {show("eyebrow") && <div className="relative z-10 text-center font-story-serif uppercase tracking-[0.2em] text-white/60" style={{ fontSize: big ? 12 : 10 }}>{ms.kicker || "Raised to the Rafters"}</div>}
            <div className="relative z-10 flex-1 flex justify-center">
                <div className="flex flex-col items-center text-center px-5 pt-4 pb-8"
                    style={{ width: big ? 168 : 138, background: `linear-gradient(180deg, ${tc} 0%, ${tc}cc 100%)`, clipPath: "polygon(0 0, 100% 0, 100% 84%, 50% 100%, 0 84%)", boxShadow: `0 12px 30px ${tc}44` }}>
                    <span className="text-[9px] font-black uppercase tracking-[0.24em] text-black/70">Milestone</span>
                    {show("number") && <span className="font-story-punch tabular-nums leading-none text-[#0A0A0C]" style={{ fontSize: big ? 72 : 56 }}>{ms.value}</span>}
                    {show("stat") && <span className="mt-1 text-[10px] font-black uppercase tracking-wider text-black/75 leading-tight px-1">{ms.name}</span>}
                    <span className="mt-3 h-6 w-6"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></span>
                </div>
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center justify-center gap-2.5 mb-3">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 44 : 38} />}
                    <div className="text-center leading-tight"><div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 24 : 19 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}</div></div>
                </div>
            )}
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}

// (10) CHIPS — stacked casino-style discs; the milestone sits on the top chip.
export function V1MilestoneChipsCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    const chip = big ? 150 : 120;
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg={`radial-gradient(110% 90% at 50% 70%, ${tc}1c 0%, #08080A 60%)`}>
            <div className="relative z-10 flex items-center justify-between">
                {show("eyebrow") && <span className="font-story-serif uppercase tracking-[0.18em]" style={{ color: tc, fontSize: big ? 13 : 11 }}>{ms.kicker || "Stacking Up"}</span>}
                {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 48 : 40} />}
            </div>
            <div className="relative z-10 flex-1 flex items-end justify-center pb-1">
                <div className="relative" style={{ width: chip, height: chip + 26 }}>
                    <div className="absolute left-0 rounded-full" style={{ width: chip, height: chip, bottom: 20, background: `${tc}30`, border: `2px dashed ${tc}66` }} />
                    <div className="absolute left-0 rounded-full" style={{ width: chip, height: chip, bottom: 10, background: `${tc}44`, border: `2px dashed ${tc}88` }} />
                    <div className="absolute left-0 rounded-full flex flex-col items-center justify-center" style={{ width: chip, height: chip, bottom: 0, background: `radial-gradient(circle, ${tc} 0%, ${tc}cc 100%)`, border: `3px dashed #0A0A0C99`, boxShadow: `0 8px 24px ${tc}55` }}>
                        {show("number") && <span className="font-story-punch tabular-nums leading-none text-[#0A0A0C]" style={{ fontSize: big ? 54 : 42 }}>{ms.value}</span>}
                        {show("stat") && <span className="text-[9px] font-black uppercase tracking-wider text-black/70 mt-0.5 px-2 text-center leading-tight">{ms.name}</span>}
                    </div>
                </div>
            </div>
            {show("name") && <div className="relative z-10 text-center mb-3"><div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 26 : 21 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div></div>}
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}

// (11) NEWSPRINT — front-page broadsheet headline in serif with a dateline.
export function V1MilestoneNewsCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(180deg,#14130F 0%,#0B0A08 100%)">
            <div className="relative z-10 flex flex-col h-full text-[#EDE6D6]">
                {show("eyebrow") && (
                    <div className="flex items-center justify-between border-b-2 pb-2 mb-2" style={{ borderColor: "#EDE6D633" }}>
                        <span className="font-story-serif uppercase tracking-[0.24em] text-[#EDE6D6]" style={{ fontSize: big ? 12 : 10 }}>The Milestone Gazette</span>
                        {show("date") && <span className="font-mono-stat text-[9px] tracking-widest text-[#EDE6D6]/60">{s.when || ""}</span>}
                    </div>
                )}
                {show("stat") && <div className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: tc }}>{ms.name}</div>}
                {show("name") && <div className="font-story-serif leading-[0.94] text-[#F5EFE2]" style={{ fontSize: big ? 40 : 31, fontWeight: 800 }}>{s.subject?.name} reaches <span style={{ color: tc }}>{ms.value}</span></div>}
                <div className="flex gap-3 mt-3 items-start">
                    {show("face") && s.subject && <div className="shrink-0 grayscale contrast-125"><Face subject={s.subject} color="#EDE6D6" px={big ? 78 : 64} /></div>}
                    {show("blurb") && <p className="leading-snug text-[#EDE6D6]/75 first-letter:text-3xl first-letter:font-story-serif first-letter:font-black first-letter:mr-1 first-letter:float-left" style={{ fontSize: big ? 12 : 11, columnCount: 1 }}>{s.blurb || `${s.subject?.name} etched another line into the record books, becoming the latest to reach the ${ms.value} ${ms.name} mark.`}</p>}
                </div>
                {show("footer") && <MileFoot tc={tc} s={s} label="Milestone Gazette" footerExtra={footerExtra} />}
            </div>
        </Shell>
    );
}

// (12) ARC — a conic progress ring wraps the number; the climb visualised.
export function V1MilestoneArcCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    const d = big ? 200 : 158;
    const pct = Math.max(8, Math.min(100, Number(ms.pct) || 100));
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="radial-gradient(120% 90% at 50% 40%,#111319 0%,#07080B 62%)">
            {show("eyebrow") && <div className="relative z-10 text-center font-story-serif uppercase tracking-[0.2em]" style={{ color: tc, fontSize: big ? 13 : 11 }}>{ms.kicker || "Milestone Reached"}</div>}
            <div className="relative z-10 flex-1 flex items-center justify-center">
                <div className="rounded-full flex items-center justify-center" style={{ width: d, height: d, background: `conic-gradient(${tc} ${pct}%, ${tc}1f 0)`, boxShadow: `0 0 34px ${tc}44` }}>
                    <div className="rounded-full flex flex-col items-center justify-center bg-[#08090C]" style={{ width: d - 26, height: d - 26 }}>
                        {show("number") && <span className="font-story-punch tabular-nums leading-none" style={{ color: tc, fontSize: big ? 78 : 60 }}>{ms.value}</span>}
                        {show("stat") && <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/70 mt-1 px-4 text-center leading-tight">{ms.name}</span>}
                    </div>
                </div>
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center justify-center gap-2.5 mb-3">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 44 : 38} />}
                    <div className="text-center leading-tight"><div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 24 : 19 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div></div>
                </div>
            )}
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}

// (13) BOARDING PASS — structured travel pass w/ fields + a barcode rail.
export function V1MilestoneBoardingCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(160deg,#0C0D11 0%,#08080A 100%)">
            <div className="relative z-10 flex-1 flex flex-col rounded-xl overflow-hidden bg-[#0E1015]" style={{ border: `1px solid ${tc}44` }}>
                <div className="flex items-center justify-between px-3 py-2" style={{ background: tc }}>
                    {show("eyebrow") && <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#0A0A0C]">{ms.kicker || "Milestone Boarding Pass"}</span>}
                    <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>
                </div>
                <div className="flex-1 flex">
                    <div className="flex-1 p-3.5 min-w-0">
                        {show("name") && <><div className="text-[9px] font-bold uppercase tracking-wider text-white/40">Passenger</div><div className="font-story-punch uppercase text-white leading-[0.9] mb-2" style={{ fontSize: big ? 28 : 22 }}>{s.subject?.name}</div></>}
                        <div className="grid grid-cols-2 gap-y-2">
                            {show("stat") && <div><div className="text-[9px] font-bold uppercase tracking-wider text-white/40">Milestone</div><div className="text-[11px] font-black uppercase text-white/85 leading-tight">{ms.name}</div></div>}
                            {show("date") && <div><div className="text-[9px] font-bold uppercase tracking-wider text-white/40">Date</div><div className="font-mono-stat text-[11px] text-white/85">{s.when || "—"}</div></div>}
                            <div><div className="text-[9px] font-bold uppercase tracking-wider text-white/40">Club</div><div className="text-[11px] font-black uppercase text-white/85">{tabbr(s.teamId)}</div></div>
                            {s.jersey && <div><div className="text-[9px] font-bold uppercase tracking-wider text-white/40">Seat</div><div className="font-mono-stat text-[11px] text-white/85">#{s.jersey}</div></div>}
                        </div>
                    </div>
                    <div className="flex flex-col items-center justify-center px-3 shrink-0 gap-2" style={{ borderLeft: `2px dashed ${tc}55`, background: `${tc}10` }}>
                        {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 48 : 40} />}
                        {show("number") && <span className="font-story-punch tabular-nums leading-none" style={{ color: tc, fontSize: big ? 60 : 46 }}>{ms.value}</span>}
                    </div>
                </div>
                <div className="h-7 flex items-center gap-[2px] px-3" style={{ background: "#0A0B0E", borderTop: `1px solid ${tc}33` }}>
                    {Array.from({ length: 34 }).map((_, i) => <span key={i} style={{ width: (i % 4 === 0) ? 3 : (i % 3 === 0 ? 2 : 1), height: 14, background: "#EDEDED" + (i % 2 ? "cc" : "77") }} />)}
                </div>
            </div>
            {show("footer") && <MileFoot tc={tc} s={s} footerExtra={footerExtra} />}
        </Shell>
    );
}

// (14) PLATE — an engraved gold trophy nameplate screwed to dark walnut.
export function V1MilestonePlateCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    const GOLD = "#D9B45B";
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg="linear-gradient(160deg,#1A130C 0%,#0C0906 100%)">
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center gap-4">
                {show("face") && s.subject && <div className="rounded-full" style={{ padding: 3, background: `linear-gradient(160deg,${GOLD},${GOLD}55)` }}><Face subject={s.subject} color={GOLD} px={big ? 72 : 60} /></div>}
                <div className="relative w-full rounded-md px-6 py-4 text-center"
                    style={{ background: "linear-gradient(180deg,#EAD489 0%,#C69A3F 48%,#B3862F 52%,#E7CE80 100%)", boxShadow: "inset 0 1px 0 #FFF6D0, inset 0 -2px 3px #6B4E14, 0 6px 14px #0009" }}>
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#7A5A1C]" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-[#7A5A1C]" />
                    {show("eyebrow") && <div className="text-[9px] font-black uppercase tracking-[0.3em] text-[#6B4E14]">{ms.kicker || "In Recognition Of"}</div>}
                    {show("number") && <div className="font-story-serif leading-none text-[#3A2A0C]" style={{ fontSize: big ? 56 : 44, fontWeight: 900 }}>{ms.value}</div>}
                    {show("stat") && <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[#5A420F]">{ms.name}</div>}
                </div>
                {show("name") && <div className="text-center leading-tight"><div className="font-story-punch uppercase" style={{ color: GOLD, fontSize: big ? 26 : 21 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}{s.jersey ? ` · #${s.jersey}` : ""}</div></div>}
            </div>
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}

// (15) CONFETTI — a joyful pop; scattered team-colour confetti behind the number.
export function V1MilestoneConfettiCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const tc = s.teamColor || tcol(s.teamId) || "#9D4CDD";
    const ms = s.milestone || {};
    const big = orientation === "v" || orientation === "slimv";
    const bits = [[12, 14, -18], [82, 10, 24], [24, 78, 12], [70, 82, -12], [46, 8, 40], [90, 46, -30], [6, 52, 16], [56, 90, -20], [34, 30, 8], [64, 24, -16]];
    return (
        <Shell color={tc} orientation={orientation} square={square} testId={testId} bg={`radial-gradient(100% 80% at 50% 20%, ${tc}24 0%, #08080A 60%)`}>
            {bits.map(([l, t, r], i) => (
                <span key={i} className="pointer-events-none absolute" style={{ left: `${l}%`, top: `${t}%`, width: i % 2 ? 7 : 10, height: i % 3 ? 10 : 6, background: i % 3 === 0 ? "#fff" : tc, opacity: 0.8, transform: `rotate(${r}deg)`, borderRadius: i % 2 ? 2 : 0 }} />
            ))}
            {show("eyebrow") && <div className="relative z-10 inline-flex items-center gap-1.5 self-center rounded-full px-3 py-1" style={{ color: tc, background: `${tc}18`, border: `1px solid ${tc}55` }}><PartyPopper className="h-3.5 w-3.5" /><span className="text-[10px] font-black uppercase tracking-[0.18em]">{ms.kicker || "Milestone!"}</span></div>}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
                {show("number") && <div className="font-story-punch tabular-nums leading-[0.82]" style={{ color: tc, fontSize: big ? 168 : 128, textShadow: `0 8px 30px ${tc}55` }}>{ms.value}</div>}
                {show("stat") && <div className="-mt-1 font-story-punch uppercase tracking-[0.12em] text-white" style={{ fontSize: big ? 20 : 16 }}>{ms.name}</div>}
            </div>
            {show("name") && (
                <div className="relative z-10 flex items-center justify-center gap-2.5 mb-3">
                    {show("face") && s.subject && <Face subject={s.subject} color={tc} px={big ? 46 : 40} />}
                    <div className="text-center leading-tight"><div className="font-story-punch uppercase text-white" style={{ fontSize: big ? 24 : 19 }}>{s.subject?.name}</div><div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{tname(s.teamId)}</div></div>
                </div>
            )}
            {show("footer") && <MileFoot tc={tc} s={s} center footerExtra={footerExtra} />}
        </Shell>
    );
}


// ═══════════════════ V3 — RECORD BREAKER (brand-new design) ═══════════════════
// Fully bespoke layout: eyebrow pill + date, split name, player CIRCLE (no bg
// photo), giant OUTLINED record number, stat rail with icons, footer w/ arrow.
// Purple accent (fixed — matches the design), not team-colour dependent.
const V3_ICONS = { goal: Goal, games: CalendarDays, legend: Trophy, trophy: Trophy };
export function V3RecordBreakerCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const P = "#A78BFA";                       // fixed lavender/purple accent
    const rec = s.record || {};
    const parts = String(s.subject?.name || "").trim().split(/\s+/);
    const last = parts.length > 1 ? parts.pop() : (parts[0] || "");
    const first = parts.join(" ");
    const metrics = Array.isArray(s.metrics) ? s.metrics.slice(0, 3) : [];
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={P} orientation={orientation} square={square} testId={testId}
            bg="linear-gradient(165deg,#0C0A14 0%,#08070B 60%,#050406 100%)">
            {/* header */}
            <div className="relative flex items-center justify-between gap-2">
                {show("eyebrow") && <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: P, background: `${P}18`, border: `1px solid ${P}55` }}><Sparkles className="h-3 w-3" />{rec.kicker || "Record Breaker"}</span>}
                {show("date") && <span className="font-mono-stat text-[10px] uppercase tracking-[0.22em] text-white/55">{s.when || ""}</span>}
            </div>
            {/* name + circle */}
            <div className="relative mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    {show("name") && <>
                        <div className="font-story-punch uppercase tracking-[0.06em] text-white/80" style={{ fontSize: big ? 18 : 15, lineHeight: 1 }}>{first}</div>
                        <div className="font-story-punch uppercase text-white leading-[0.86]" style={{ fontSize: big ? 58 : 46 }}>{last}</div>
                    </>}
                    {show("team") && <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/60">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        <span className="truncate">{tname(s.teamId)}</span>
                        {s.jersey && <><span className="text-white/25">|</span><span style={{ color: P }}>#{s.jersey}</span></>}
                    </div>}
                </div>
                {show("face") && s.subject && <div className="shrink-0"><Face subject={s.subject} color={P} px={big ? 96 : 80} /></div>}
            </div>
            {/* record + stat rail */}
            <div className="relative mt-auto grid grid-cols-[1.1fr_1fr] gap-3 items-end pt-3">
                <div className="min-w-0">
                    {show("record") && <>
                        <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-0.5" style={{ color: P }}>{rec.label || "New PWHL Record"}</div>
                        <div className="font-story-punch tabular-nums leading-[0.8]" style={{ fontSize: big ? 108 : 84, color: "transparent", WebkitTextStroke: `2px ${P}`, textStroke: `2px ${P}` }}>{rec.value}</div>
                        <div className="mt-1 font-story-punch uppercase text-white" style={{ fontSize: big ? 15 : 13 }}>{rec.name}</div>
                        <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: P }} />
                    </>}
                    {show("blurb") && s.blurb && <p className="mt-2.5 leading-snug text-white/60" style={{ fontSize: big ? 12 : 11 }}>{s.blurb}</p>}
                </div>
                {show("stats") && metrics.length > 0 && (
                    <div className="space-y-2.5">
                        {metrics.map((m, i) => { const Ic = V3_ICONS[m.icon] || Trophy; return (
                            <div key={i} className="flex items-center gap-2.5">
                                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${P}16`, border: `1px solid ${P}44`, color: P }}><Ic className="h-4 w-4" /></span>
                                <div className="min-w-0 leading-none">
                                    <div className="font-story-punch tabular-nums text-white" style={{ fontSize: big ? 22 : 18 }}>{m.value}</div>
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-white/45 mt-0.5">{m.label}</div>
                                </div>
                            </div>
                        ); })}
                    </div>
                )}
            </div>
            {/* footer */}
            {show("footer") && (
                <div className="relative mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: `${P}2a` }}>
                    <span className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50">{s.footerLabel || "PWHL History"}</span>
                    {footerExtra || <span className="inline-flex h-7 w-7 items-center justify-center rounded-full" style={{ border: `1px solid ${P}66`, color: P }}><ArrowUpRight className="h-3.5 w-3.5" /></span>}
                </div>
            )}
        </Shell>
    );
}

// ═══════════════════ V3 — MILESTONE (gold companion) ═══════════════════
// Same bespoke prestige layout as the Record Breaker — eyebrow pill + date,
// split name, player CIRCLE, giant OUTLINED milestone number, icon stat rail
// and footer w/ arrow. GOLD accent (fixed) so it reads as a career milestone,
// not a broken record.
const V3_MS_ICONS = { goal: Goal, games: CalendarDays, medal: Medal, trophy: Trophy, legend: Crown, streak: Flame };
export function V3MilestoneCard({ s = {}, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const P = "#E8B84B";                       // fixed gold accent
    const ms = s.milestone || s.record || {};
    const parts = String(s.subject?.name || "").trim().split(/\s+/);
    const last = parts.length > 1 ? parts.pop() : (parts[0] || "");
    const first = parts.join(" ");
    const metrics = Array.isArray(s.metrics) ? s.metrics.slice(0, 3) : [];
    const big = orientation === "v" || orientation === "slimv";
    return (
        <Shell color={P} orientation={orientation} square={square} testId={testId}
            bg="linear-gradient(165deg,#12100A 0%,#0A0906 60%,#050403 100%)">
            {/* header */}
            <div className="relative flex items-center justify-between gap-2">
                {show("eyebrow") && <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: P, background: `${P}18`, border: `1px solid ${P}55` }}><Medal className="h-3 w-3" />{ms.kicker || "Milestone"}</span>}
                {show("date") && <span className="font-mono-stat text-[10px] uppercase tracking-[0.22em] text-white/55">{s.when || ""}</span>}
            </div>
            {/* name + circle */}
            <div className="relative mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    {show("name") && <>
                        <div className="font-story-punch uppercase tracking-[0.06em] text-white/80" style={{ fontSize: big ? 18 : 15, lineHeight: 1 }}>{first}</div>
                        <div className="font-story-punch uppercase text-white leading-[0.86]" style={{ fontSize: big ? 58 : 46 }}>{last}</div>
                    </>}
                    {show("team") && <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/60">
                        {s.teamId && <span className="h-4 w-4"><TeamBadge teamId={s.teamId} size="xs" asLink={false} /></span>}
                        <span className="truncate">{tname(s.teamId)}</span>
                        {s.jersey && <><span className="text-white/25">|</span><span style={{ color: P }}>#{s.jersey}</span></>}
                    </div>}
                </div>
                {show("face") && s.subject && <div className="shrink-0"><Face subject={s.subject} color={P} px={big ? 96 : 80} /></div>}
            </div>
            {/* milestone + stat rail */}
            <div className="relative mt-auto grid grid-cols-[1.1fr_1fr] gap-3 items-end pt-3">
                <div className="min-w-0">
                    {show("record") && <>
                        <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-0.5" style={{ color: P }}>{ms.label || "Career Milestone"}</div>
                        <div className="font-story-punch tabular-nums leading-[0.8]" style={{ fontSize: big ? 108 : 84, color: "transparent", WebkitTextStroke: `2px ${P}`, textStroke: `2px ${P}` }}>{ms.value}</div>
                        <div className="mt-1 font-story-punch uppercase text-white" style={{ fontSize: big ? 15 : 13 }}>{ms.name}</div>
                        <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: P }} />
                    </>}
                    {show("blurb") && s.blurb && <p className="mt-2.5 leading-snug text-white/60" style={{ fontSize: big ? 12 : 11 }}>{s.blurb}</p>}
                </div>
                {show("stats") && metrics.length > 0 && (
                    <div className="space-y-2.5">
                        {metrics.map((m, i) => { const Ic = V3_MS_ICONS[m.icon] || Medal; return (
                            <div key={i} className="flex items-center gap-2.5">
                                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${P}16`, border: `1px solid ${P}44`, color: P }}><Ic className="h-4 w-4" /></span>
                                <div className="min-w-0 leading-none">
                                    <div className="font-story-punch tabular-nums text-white" style={{ fontSize: big ? 22 : 18 }}>{m.value}</div>
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-white/45 mt-0.5">{m.label}</div>
                                </div>
                            </div>
                        ); })}
                    </div>
                )}
            </div>
            {/* footer */}
            {show("footer") && (
                <div className="relative mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: `${P}2a` }}>
                    <span className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50">{s.footerLabel || "PWHL Milestones"}</span>
                    {footerExtra || <span className="inline-flex h-7 w-7 items-center justify-center rounded-full" style={{ border: `1px solid ${P}66`, color: P }}><ArrowUpRight className="h-3.5 w-3.5" /></span>}
                </div>
            )}
        </Shell>
    );
}

// ============================================================================
// V1 · TEN NEW DESIGNS — each an original visual concept (not a re-skin).
// Shared helpers: Shell, Crest, Face, StoryLogo, Pills, MoreLink, tcol/tnick/tabbr.
// ============================================================================
const vFrame = (o) => (o === "v" || o === "slimv") ? "aspect-[4/5]" : "h-full min-h-[236px]";

// (1) POLAROID — a tilted instant photo with tape + a handwritten caption.
export function V1PolaroidCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 p-6 flex items-center justify-center ${vFrame(orientation)}`} style={{ background: `radial-gradient(120% 120% at 50% 0%, ${color}22, transparent 60%), #0A0B0D` }}>
            <div className="relative bg-[#f7f5ef] p-3 pb-10 shadow-2xl shadow-black/60" style={{ transform: "rotate(-3.5deg)", width: "78%" }}>
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-24 bg-white/25 backdrop-blur-sm border border-white/30" style={{ transform: "rotate(2deg)" }} />
                <div className="relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${color}, #0E1110)`, aspectRatio: "1/1" }}>
                    {show("face") && s.subject && <div className="absolute inset-0 flex items-end justify-center"><PlayerAvatar photo={s.subject?.photo_url} name={s.subject?.name} playerId={s.subject?.id || ""} sizePx={220} showTierRing={false} /></div>}
                    {show("pills") && !!(s.pills || []).length && <span className="absolute top-2 left-2 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: color, color: "#fff" }}>{s.pills[0]}</span>}
                </div>
                <div className="mt-3 text-center">
                    <div className="font-story-serif italic text-[#1a1a1a] text-xl leading-none">{s.line1 || s.subject?.name}</div>
                    {show("line2") && <div className="font-story-serif italic text-[#7a7368] text-sm mt-1">{s.line2 || ""}</div>}
                </div>
            </div>
        </article>
    );
}

// (2) DIAGONAL SCOREBOARD — two team colours split on a hard diagonal, giant score.
export function V1DiagScoreCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const away = s.oppId, home = s.teamId;
    const ac = tcol(away), hc = tcol(home);
    const hs = s.score?.home, as = s.score?.away;
    const post = !!s.score;
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 ${vFrame(orientation)}`}>
            <div className="absolute inset-0" style={{ background: ac }} />
            <div className="absolute inset-0" style={{ background: hc, clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45))" }} />
            <div className="relative z-10 h-full flex flex-col p-5">
                {show("status") && <div className="text-center text-white text-[11px] font-black uppercase tracking-[0.3em]">{post ? (s.status || "FINAL") : (s.when || "PREVIEW")}</div>}
                <div className="flex-1 flex items-center justify-between">
                    <div className="flex flex-col items-start gap-2">
                        {show("face") && <StoryLogo id={away} px={orientation === "v" ? 72 : 58} />}
                        <span className="font-story-punch text-white text-lg tracking-widest">{tabbr(away)}</span>
                        {post && <span className="font-story-punch text-white text-6xl leading-none tabular-nums drop-shadow-lg">{as}</span>}
                    </div>
                    <span className="font-story-punch italic text-white/80 text-3xl">{post ? "" : "@"}</span>
                    <div className="flex flex-col items-end gap-2">
                        {show("face") && <StoryLogo id={home} px={orientation === "v" ? 72 : 58} />}
                        <span className="font-story-punch text-white text-lg tracking-widest">{tabbr(home)}</span>
                        {post && <span className="font-story-punch text-white text-6xl leading-none tabular-nums drop-shadow-lg">{hs}</span>}
                    </div>
                </div>
                {show("footer") && <div className="text-center text-white/80 text-[10px] font-bold uppercase tracking-widest">{tabbr(away)} @ {tabbr(home)}</div>}
            </div>
        </article>
    );
}

// (3) STAT DONUT — an SVG progress ring wrapping a face, one hero percentage.
export function V1StatDonutCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const pct = Math.max(0, Math.min(100, Number(s.pct ?? 72)));
    const R = 74, C = 2 * Math.PI * R;
    return (
        <Shell color={color} orientation={orientation} square={square} testId={testId}>
            <div className="flex items-center justify-between"><Pills pills={s.pills} color={color} show={show} />{show("opp") && s.oppId && <TeamBadge teamId={s.oppId} size="xs" asLink={false} />}</div>
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="relative" style={{ width: 176, height: 176 }}>
                    <svg width="176" height="176" className="-rotate-90"><circle cx="88" cy="88" r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" /><circle cx="88" cy="88" r={R} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} /></svg>
                    <div className="absolute inset-0 flex items-center justify-center">{show("face") && s.subject ? <Face subject={s.subject} color={color} px={112} /> : <span className="font-story-punch text-white text-5xl tabular-nums">{pct}%</span>}</div>
                </div>
                <div className="text-center">
                    {show("line2") && <div className="font-story-punch uppercase text-white text-2xl leading-none">{s.subject?.name || s.line1}</div>}
                    {show("stat") && <div className="mt-1 text-[12px] font-black uppercase tracking-wider tabular-nums" style={{ color }}>{s.statLine || `${pct}% ${s.metric || ""}`}</div>}
                </div>
            </div>
            {show("footer") && <div className="mt-auto pt-3 border-t border-white/[0.07] flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/50">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || tname(s.teamId)}</span>{footerExtra || <MoreLink source={s.sourceRef} color={color} />}</div>}
        </Shell>
    );
}

// (4) MAGAZINE COVER — masthead, full-bleed face, big cover line, issue barcode.
export function V1MagazineCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 ${vFrame(orientation)}`} style={{ background: `linear-gradient(180deg, ${color}, #0A0B0D 78%)` }}>
            {show("face") && s.subject && <div className="absolute inset-x-0 bottom-0 flex justify-center"><PlayerAvatar photo={s.subject?.photo_url} name={s.subject?.name} playerId={s.subject?.id || ""} sizePx={orientation === "v" ? 300 : 220} showTierRing={false} /></div>}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 35%, rgba(0,0,0,0.6) 100%)" }} />
            <div className="relative z-10 h-full flex flex-col p-5">
                <div className="flex items-center justify-between border-b border-white/25 pb-2">
                    <span className="font-story-punch text-white text-2xl tracking-tight">{s.masthead || "ROSTERLY"}</span>
                    <span className="text-white/70 text-[9px] font-black uppercase tracking-widest">{s.issue || "Issue 01"}</span>
                </div>
                <div className="mt-auto">
                    {show("pills") && !!(s.pills || []).length && <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: color, color: "#fff" }}>{s.pills[0]}</span>}
                    <div className="font-story-punch uppercase text-white leading-[0.92] mt-2" style={{ fontSize: orientation === "v" ? 44 : 34, textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>{s.line1 || s.subject?.name}</div>
                    {show("line2") && <div className="font-story-serif italic text-white/85 text-base mt-1">{s.line2 || ""}</div>}
                    <div className="mt-3 flex items-end gap-0.5 h-7 opacity-80">{Array.from({ length: 26 }).map((_, i) => <span key={i} style={{ width: 2, height: "100%", background: i % 3 ? "#fff" : "transparent", opacity: (i % 4) ? 1 : 0.5 }} />)}</div>
                </div>
            </div>
        </article>
    );
}

// (5) TRADING CARD — a collectible: foil border, position badge, mini stat table.
export function V1TradingCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const stats = s.stats || [{ k: "GP", v: "24" }, { k: "G", v: "12" }, { k: "PTS", v: "31" }];
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} ${vFrame(orientation)}`} style={{ padding: 8, background: `linear-gradient(135deg, ${color}, #d9c27a 45%, ${color} 90%)` }}>
            <div className="relative h-full w-full rounded-xl overflow-hidden flex flex-col" style={{ background: "linear-gradient(180deg,#12131a,#0a0b0d)" }}>
                <div className="flex items-center justify-between px-3 pt-3">
                    <span className="font-story-punch text-white text-sm tracking-widest">{s.subject?.name || s.line1}</span>
                    {s.pos && <span className="h-7 w-7 rounded-full grid place-items-center font-story-punch text-white text-xs" style={{ background: color }}>{s.pos}</span>}
                </div>
                <div className="relative flex-1 flex items-end justify-center" style={{ background: `radial-gradient(90% 80% at 50% 20%, ${color}44, transparent 70%)` }}>
                    {show("face") && s.subject && <PlayerAvatar photo={s.subject?.photo_url} name={s.subject?.name} playerId={s.subject?.id || ""} sizePx={orientation === "v" ? 210 : 150} showTierRing={false} />}
                    {s.teamId && <span className="absolute top-2 right-2 opacity-90"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></span>}
                </div>
                <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10">
                    {stats.slice(0, 3).map((st, i) => <div key={i} className="py-2 text-center"><div className="font-story-punch text-white text-xl tabular-nums leading-none">{st.v}</div><div className="text-white/45 text-[9px] font-black uppercase tracking-widest mt-0.5">{st.k}</div></div>)}
                </div>
            </div>
        </article>
    );
}

// (6) BROADCAST CHYRON — a TV lower-third with kicker, headline bar + ticker.
export function V1ChyronCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 ${vFrame(orientation)}`} style={{ background: `radial-gradient(120% 90% at 20% 10%, ${color}33, transparent 55%), #08090B` }}>
            {show("face") && s.subject && <div className="absolute right-0 bottom-10 opacity-90"><PlayerAvatar photo={s.subject?.photo_url} name={s.subject?.name} playerId={s.subject?.id || ""} sizePx={orientation === "v" ? 260 : 190} showTierRing={false} /></div>}
            <div className="absolute inset-x-0 bottom-0 z-10">
                <div className="mx-4 mb-2">
                    <span className="inline-block font-story-punch text-white text-[11px] tracking-[0.25em] px-3 py-1" style={{ background: color }}>{(s.pills && s.pills[0]) || "BREAKING"}</span>
                    <div className="bg-black/85 backdrop-blur-sm border-l-4 px-3 py-2.5" style={{ borderColor: color }}>
                        <div className="font-story-punch uppercase text-white leading-none" style={{ fontSize: orientation === "v" ? 28 : 22 }}>{s.line1}</div>
                        {show("line2") && <div className="font-story-serif italic text-white/70 text-sm mt-1">{s.line2}</div>}
                    </div>
                </div>
                <div className="overflow-hidden whitespace-nowrap py-1.5" style={{ background: color }}>
                    <span className="font-story-punch text-white/90 text-[11px] tracking-widest px-4">{(s.ticker || "PWHL · LIVE UPDATES · ") .repeat(4)}</span>
                </div>
            </div>
        </article>
    );
}

// (7) COMIC POP — halftone pop-art panel with a burst behind a hero number/word.
export function V1ComicCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const burst = Array.from({ length: 16 }).map((_, i) => `${(i / 16) * 360}deg`);
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border-4 border-black ${vFrame(orientation)}`} style={{ background: "#f4ecd8" }}>
            <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(${color}55 22%, transparent 23%)`, backgroundSize: "12px 12px" }} />
            <div className="absolute inset-0" style={{ background: `conic-gradient(from 0deg, ${color} 0 6deg, transparent 6deg 22.5deg)`, opacity: 0.18, clipPath: "circle(60% at 50% 42%)" }} />
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-5 text-center">
                {show("pills") && !!(s.pills || []).length && <span className="font-story-punch text-white text-sm px-3 py-1 -rotate-3" style={{ background: "#111", border: `2px solid ${color}` }}>{s.pills[0]}</span>}
                <div className="font-story-punch uppercase leading-[0.85] text-black mt-3" style={{ fontSize: orientation === "v" ? 60 : 46, WebkitTextStroke: `2px #111`, textShadow: `4px 4px 0 ${color}` }}>{s.hero || s.line1}</div>
                {show("line2") && <div className="mt-3 font-story-punch uppercase text-black text-lg bg-white border-2 border-black px-3 py-1" style={{ transform: "rotate(-1.5deg)" }}>{s.line2 || s.subject?.name}</div>}
            </div>
        </article>
    );
}

// (8) BLUEPRINT — technical schematic look: grid, cyan hairlines, corner ticks.
export function V1BlueprintCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const line = "rgba(120,180,255,0.28)";
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 ${vFrame(orientation)}`} style={{ background: "#0a1830" }}>
            <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`, backgroundSize: "22px 22px" }} />
            <div className="absolute inset-3 border" style={{ borderColor: "rgba(120,180,255,0.4)" }} />
            <div className="relative z-10 h-full flex flex-col p-6">
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest" style={{ color: "#bcd8ff" }}><span>{s.docNo || "FIG. 01"}</span><span>{s.scale || "SCALE 1:1"}</span></div>
                <div className="flex-1 flex items-center justify-center">
                    {show("face") && s.subject ? <div className="rounded-full p-[3px]" style={{ border: `1px dashed rgba(120,180,255,0.6)` }}><PlayerAvatar photo={s.subject?.photo_url} name={s.subject?.name} playerId={s.subject?.id || ""} sizePx={orientation === "v" ? 150 : 120} showTierRing={false} /></div> : <StoryLogo id={s.teamId} px={orientation === "v" ? 140 : 110} />}
                </div>
                <div>
                    <div className="font-story-punch uppercase leading-none" style={{ color: "#eaf3ff", fontSize: orientation === "v" ? 30 : 24 }}>{s.line1 || s.subject?.name}</div>
                    {show("line2") && <div className="font-mono text-[12px] mt-1" style={{ color: "#9fc3f5" }}>{s.line2 || ""}</div>}
                    {show("stat") && s.statLine && <div className="mt-2 inline-block font-mono text-[11px] px-2 py-1" style={{ color: "#0a1830", background: "#bcd8ff" }}>{s.statLine}</div>}
                </div>
            </div>
        </article>
    );
}

// (9) DEAL SLIP — a receipt/itemised slip for a signing or trade (monospace).
export function V1ReceiptCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    const rows = s.terms || [{ k: "Term", v: "3 Years" }, { k: "AAV", v: "$—" }, { k: "Type", v: "Standard" }];
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 flex items-center justify-center p-5 ${vFrame(orientation)}`} style={{ background: `radial-gradient(120% 100% at 50% 0%, ${color}26, transparent 55%), #0A0B0D` }}>
            <div className="relative bg-[#f7f4ea] text-[#1a1a1a] w-[80%] p-4 shadow-2xl shadow-black/60" style={{ clipPath: "polygon(0 0,100% 0,100% 97%,95% 100%,90% 97%,85% 100%,80% 97%,75% 100%,70% 97%,65% 100%,60% 97%,55% 100%,50% 97%,45% 100%,40% 97%,35% 100%,30% 97%,25% 100%,20% 97%,15% 100%,10% 97%,5% 100%,0 97%)" }}>
                <div className="text-center border-b border-dashed border-black/30 pb-2">
                    <div className="font-story-punch uppercase text-lg leading-none">{(s.pills && s.pills[0]) || "Official Deal"}</div>
                    <div className="font-mono text-[10px] text-black/55 mt-1">{s.when || "TODAY"} · {tabbr(s.teamId)}</div>
                </div>
                <div className="font-story-punch uppercase text-xl text-center mt-2">{s.subject?.name || s.line1}</div>
                <div className="mt-2 space-y-1 font-mono text-[12px]">
                    {rows.map((r, i) => <div key={i} className="flex justify-between border-b border-dotted border-black/20 pb-0.5"><span className="text-black/60">{r.k}</span><span className="font-bold">{r.v}</span></div>)}
                </div>
                <div className="mt-3 flex items-end gap-0.5 h-8 justify-center">{Array.from({ length: 30 }).map((_, i) => <span key={i} style={{ width: 2, height: "100%", background: i % 2 ? "#111" : "transparent" }} />)}</div>
            </div>
        </article>
    );
}

// (10) SPOTLIGHT — a dark stage with a single spotlight cone on a centred face.
export function V1SpotlightCard({ s, orientation = "v", square = false, hideEls = [], testId, footerExtra }) {
    const show = (k) => !hideEls.includes(k);
    const color = s.teamColor || tcol(s.teamId);
    return (
        <article data-testid={testId} data-orientation={orientation} className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-2xl"} border border-white/10 ${vFrame(orientation)}`} style={{ background: "#050506" }}>
            <div className="absolute left-1/2 -top-6 -translate-x-1/2 pointer-events-none" style={{ width: "120%", height: "90%", background: `radial-gradient(50% 60% at 50% 0%, ${color}55, transparent 70%)`, clipPath: "polygon(42% 0, 58% 0, 100% 100%, 0% 100%)", opacity: 0.85 }} />
            <div className="relative z-10 h-full flex flex-col items-center justify-center p-5 text-center">
                {show("pills") && !!(s.pills || []).length && <span className="text-[10px] font-black uppercase tracking-[0.3em] mb-3" style={{ color }}>{s.pills[0]}</span>}
                {show("face") && s.subject && <Face subject={s.subject} color={color} px={orientation === "v" ? 150 : 120} />}
                <div className="font-story-punch uppercase text-white leading-none mt-4" style={{ fontSize: orientation === "v" ? 34 : 26 }}>{s.subject?.name || s.line1}</div>
                {show("line2") && <div className="font-story-serif italic text-white/60 text-base mt-1">{s.line2 || ""}</div>}
                {show("stat") && s.statLine && <div className="mt-2 text-[12px] font-black uppercase tracking-wider tabular-nums" style={{ color }}>{s.statLine}</div>}
            </div>
            {show("footer") && <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/40">{s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}{s.when || tname(s.teamId)}</div>}
        </article>
    );
}

// INTERNATIONAL board (Women's Worlds): a titled list of flag rows — today's games, results or group standings.
export function IntBoardCard({ s, orientation = "v", square = false, testId, footerExtra }) {
    const accent = s.teamColor || "#E11D48";
    const dense = (s.groups || []).reduce((n, g) => n + g.rows.length, 0) > 6;  // full group tables: compact rows so the board fits the card
    const flag = (iso) => (iso ? <img src={`https://flagcdn.com/w80/${iso}.png`} alt="" className={`${dense ? "h-3 w-[18px]" : "h-4 w-6"} object-cover rounded-[3px] shrink-0`} /> : <span className="h-4 w-6 shrink-0" />);
    return (
        <Shell color={accent} orientation={orientation} square={square} testId={testId}>
            <div className={`flex items-center gap-1.5 min-w-0 ${dense ? "mb-1" : "mb-2"}`}>
                <span className="inline-flex items-center min-w-0 rounded-full px-2 py-1" style={{ background: `${accent}1f`, border: `1px solid ${accent}59` }} data-testid="int-board-title">
                    <span className="font-story-punch uppercase text-[12px] tracking-[0.08em] leading-none whitespace-nowrap truncate" style={{ color: accent }}>{s.title}</span>
                </span>
                <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider rounded-full whitespace-nowrap text-white/55 border border-white/15">{s.stage || "Women's Worlds"}</span>
            </div>
            <div className={`flex-1 min-h-0 overflow-hidden flex flex-col justify-center ${dense ? "gap-1 [&_[data-int-row]]:py-0 [&_[data-int-row]]:h-[13px] [&_[data-int-row]_span]:!text-[10px] [&>div]:gap-[2px]" : "gap-2.5"}`}>
                {(s.groups || []).map((g) => (
                    <div key={g.label || "all"} className="flex flex-col gap-1">
                        {g.label && <span className={`${dense ? "text-[8px] leading-none" : "text-[9px]"} font-black uppercase tracking-widest text-white/40 px-1`}>{g.label}</span>}
                        {g.rows.map((r, i) => (
                            <div key={i} data-int-row className="relative flex items-center gap-2 rounded-lg pl-3 pr-2.5 py-1.5 overflow-hidden bg-white/[0.04]" data-testid={`int-board-row-${i}`}>
                                <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg" style={{ background: accent }} />
                                {r.rank != null && <span className="font-story-punch text-sm tabular-nums text-white/55 w-4 shrink-0">{r.rank}</span>}
                                {flag(r.iso)}<span className="font-story-punch uppercase text-xs sm:text-sm text-white">{r.code}</span>
                                {r.vs && <><span className="text-[9px] font-black text-white/35">{r.vsLabel || "vs"}</span>{flag(r.vs.iso)}<span className="font-story-punch uppercase text-xs sm:text-sm text-white">{r.vs.code}</span></>}
                                <span className="ml-auto text-[11px] tabular-nums text-white/75 font-bold whitespace-nowrap">{r.right}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div className={`${dense ? "mt-1.5 pt-1.5" : "mt-3 pt-3"} border-t border-white/[0.07] flex items-center justify-between`}>
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">{s.footerLabel || "Women's Worlds"}</span>
                {footerExtra || <MoreLink source={s.sourceRef} color={accent} label="WWHC" />}
            </div>
        </Shell>
    );
}

export const GAME_LAYOUTS = {
    int_board: IntBoardCard,
    game_award: GameAwardCard,
    game_vs: GameVsCard,
    v1_polaroid: V1PolaroidCard,
    v1_diag_score: V1DiagScoreCard,
    v1_stat_donut: V1StatDonutCard,
    v1_magazine: V1MagazineCard,
    v1_trading_card: V1TradingCard,
    v1_chyron: V1ChyronCard,
    v1_comic: V1ComicCard,
    v1_blueprint: V1BlueprintCard,
    v1_receipt: V1ReceiptCard,
    v1_spotlight: V1SpotlightCard,
    game_today: GameTodayCard,
    game_today_preview: GameTodayCard,
    game_today_recap: GameTodayCard,
    game_next: NextGameCard,
    game_next_slate: NextGamesSlateCard,
    game_stars3: GameStars3Card,
    game_draft: GameDraftCard,
    draft_round6: DraftRoundCard,
    draft_round8: DraftRoundCard,
    draft_flashback: DraftFlashbackCard,
    standings_table: StandingsCard,
    pbp_card: PbpCard,
    misc_birthday: BirthdayCard,
    lineup_unit: LineupUnitCard,
    game_goalie: GameGoalieCard,
    game_goalie_duo: GameGoalieDuoCard,
    game_milestone: GameMilestoneCard,
    game_record: GameRecordCard,
    game_milestone_prestige: GameMilestonePrestigeCard,
    game_record_prestige: GameRecordPrestigeCard,
    v1_news: V1NewsCard,
    v1_signing: V1NewsCard,
    v1_resign: V1NewsCard,
    v1_trade: V1NewsCard,
    v3_record_breaker: V3RecordBreakerCard,
    v3_milestone: V3MilestoneCard,
    v1_milestone_marquee: V1MilestoneMarqueeCard,
    v1_milestone_number: V1MilestoneNumberCard,
    v1_milestone_ticket: V1MilestoneTicketCard,
    v1_milestone_timeline: V1MilestoneTimelineCard,
    v1_milestone_medallion: V1MilestoneMedallionCard,
    v1_milestone_stamp: V1MilestoneStampCard,
    v1_milestone_neon: V1MilestoneNeonCard,
    v1_milestone_split: V1MilestoneSplitCard,
    v1_milestone_banner: V1MilestoneBannerCard,
    v1_milestone_chips: V1MilestoneChipsCard,
    v1_milestone_news: V1MilestoneNewsCard,
    v1_milestone_arc: V1MilestoneArcCard,
    v1_milestone_boarding: V1MilestoneBoardingCard,
    v1_milestone_plate: V1MilestonePlateCard,
    v1_milestone_confetti: V1MilestoneConfettiCard,
    game_vs_series: GameSeriesCard,
    game_gd: GameGDCard,
    game_trade: GameTradeCard,
    game_flashback: GameFlashbackCard,
};
